import React, { useMemo } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import LandingPage from "./views/LandingPage.js";
import LoginPage from "./views/LoginPage.js";
import PrivacyPolicy from "./views/PrivacyPolicy.js";
import AboutUs from "./views/AboutUs";
import AuthLoading from './views/AuthLoading';
import { Provider, useSelector } from "react-redux";
import ProtectedRoute from './views/ProtectedRoute';
import MyProfile from './views/MyProfile';
import BookingHistory from './views/BookingHistory';
import Dashboard from './views/Dashboard';
import Storefront from './views/Storefront';
import Shop from "./views/Shop";
import ShopProduct from "./views/ShopProduct";
import ShopCategory from "./views/ShopCategory";
import ShopCart from "./views/ShopCart";
import ShopCheckout from "./views/ShopCheckout";
import Subscriptions from "./views/Subscriptions";
import Auctions from "./views/Auctions";
import AuctionDetail from "./views/AuctionDetail";
import CarTypes from './views/CarTypes';
import AddBookings from './views/AddBookings';
import Promos from './views/Promos';
import Users from './views/Users';
import Riders from './views/Riders';
import Drivers from './views/Drivers';
import FleetAdmins from './views/FleetAdmins';
import CreateAdmin from './views/CreateAdmin';
import Notifications from './views/Notifications';
import Settings from './views/Settings.js';
import LanguageSettings from './views/LanguageSettings';
import CancellationReasons from './views/CancellationReasons';
import AddMoney from "./views/AddMoney";
import Withdraws from './views/Withdraws';
import AllReports from "./views/AllReports";
import DriverEarning from "./views/DriverEarning";
import Earningreports from "./views/Earningreports";
import TaxiOwners from "./views/TaxiOwners";
import TaxiInformation from "./views/TaxiInformation";
import IncomeShiftReport from "./views/IncomeShiftReport";
import CompetitorPrices from "./views/CompetitorPrices";
import TurPrisMatch from "./views/TurPrisMatch";
import TurPrisLanding from "./views/TurPrisLanding";
import TurPrisAdmin from "./views/TurPrisAdmin";
import PartnerPortal from "./views/PartnerPortal";
import PartnerLandingPage from "./views/PartnerLandingPage";
import PartnerBookingPage from "./views/PartnerBookingPage";
import Zones from './views/Zones';
import AppInformation from './views/AppInformation';
import { FirebaseProvider, store } from "common";
import { FirebaseConfig } from './config/FirebaseConfig';
import { GoogleMapApiConfig } from './config/GoogleMapApiConfig';
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import 'moment/min/locales';
import ContactUs from "./views/ContactUs";
import UserWallet from "./views/UserWallet";
import CarsList from "./views/CarsList";
import {ThemeProvider} from '@mui/styles';
import {createTheme, CssBaseline, Box} from '@mui/material';
import SnackbarProvider from './components/SnackbarProvider.jsx';
import { useJsApiLoader } from '@react-google-maps/api';
import MobileNav from './components/MobileNav.jsx';
import { colors } from './components/Theme/WebTheme';

const libraries = ['geometry','drawing','places'];

const ADMIN_THEME_PRESETS = {
  slate: {
    light: {
      primary: '#0f172a',
      secondary: '#1f2937',
      accent: '#38bdf8',
      background: '#f8fafc',
      paper: '#ffffff',
      text: '#0f172a',
      muted: '#475569',
    },
    dark: {
      primary: '#38bdf8',
      secondary: '#0ea5e9',
      accent: '#22d3ee',
      background: '#0b1120',
      paper: '#111827',
      text: '#f8fafc',
      muted: '#94a3b8',
    },
  },
  amber: {
    light: {
      primary: '#b45309',
      secondary: '#92400e',
      accent: '#f59e0b',
      background: '#fffbeb',
      paper: '#ffffff',
      text: '#1f2937',
      muted: '#92400e',
    },
    dark: {
      primary: '#fbbf24',
      secondary: '#f59e0b',
      accent: '#f97316',
      background: '#1f1307',
      paper: '#2a1c0f',
      text: '#fef3c7',
      muted: '#fcd34d',
    },
  },
  emerald: {
    light: {
      primary: '#047857',
      secondary: '#065f46',
      accent: '#10b981',
      background: '#f0fdf4',
      paper: '#ffffff',
      text: '#0f172a',
      muted: '#047857',
    },
    dark: {
      primary: '#34d399',
      secondary: '#10b981',
      accent: '#84cc16',
      background: '#062e1f',
      paper: '#0b3b2b',
      text: '#ecfdf5',
      muted: '#a7f3d0',
    },
  },
};

const resolveAdminTheme = (settings) => {
  const adminTheme = settings?.adminTheme || {};
  const mode = adminTheme.mode === 'dark' ? 'dark' : 'light';
  const presetKey = adminTheme.preset || 'slate';
  const preset = ADMIN_THEME_PRESETS[presetKey] || ADMIN_THEME_PRESETS.slate;
  const palette = preset[mode] || preset.light;

  return {
    palette: {
      mode,
      primary: { main: palette.primary || colors.PRIMARY },
      secondary: { main: palette.accent || colors.ACCENT_SOLID },
      background: {
        default: palette.background || colors.LandingPage_Background,
        paper: palette.paper || colors.WHITE,
      },
      text: {
        primary: palette.text || colors.DARK,
        secondary: palette.muted || '#4b5563',
      },
    },
    shape: { borderRadius: 12 },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: palette.background || colors.LandingPage_Background,
            color: palette.text || colors.DARK,
            margin: 0,
            padding: 0,
            overflow: 'auto',
          },
          html: {
            overflow: 'auto',
          },
          '#root': {
            minHeight: '100vh',
          },
          a: {
            color: palette.primary || colors.PRIMARY,
          },
        },
      },
    },
  };
};

// Default English fallbacks for new UI strings
const defaultEn = {
  // Navigation
  ride: 'Ride',
  drive: 'Drive',
  storefront: 'Storefront',
  storefront_subtitle: 'Customize the main homepage experience for the primary domain.',
  about: 'About',
  company: 'Company',
  products: 'Products',
  business: 'Business',
  privacy: 'Privacy',
  terms: 'Terms',
  language: 'Language',
  shop: 'Shop',
  cart: 'Cart',
  checkout: 'Checkout',
  // Storefront sections / navigation / appearance
  sections_help: 'Enable, disable, and reorder sections on the main landing page.',
  navigation: 'Navigation',
  navigation_help: 'Edit header and footer links for the main domain website.',
  appearance: 'Appearance',
  appearance_help: 'Override colors and header/footer styling for the selected template.',
  save_draft: 'Save draft',
  publish_live: 'Publish live',
  current_live_template: 'Current live template',
  draft_selection: 'Draft selection',
  saved_draft: 'Saved draft',
  not_saved: 'Not saved',
  saved: 'Saved',
  draft: 'Draft',
  dark: 'Dark',
  light: 'Light',
  dark_mode: 'Dark mode',
  transparent: 'Transparent',
  reset_order: 'Reset order',
  enabled: 'Enabled',
  disabled: 'Disabled',
  add: 'Add',
  auth: 'Auth',
  tip_translation_keys: 'Tip: you can use translation keys (e.g. shop)',
  ride_menu_links: 'Ride menu links',
  ride_menu_help: 'Shown in the header under Ride.',
  drive_menu_links: 'Drive menu links',
  drive_menu_help: 'Shown in the header under Drive.',
  about_menu_links: 'About menu links',
  about_menu_help: 'Shown in the header under About.',
  header: 'Header',
  footer: 'Footer',
  header_mode: 'Header mode',
  nav_mode: 'Nav mode',
  header_scroll_mode: 'Header on scroll mode',
  footer_mode: 'Footer mode',
  footer_background: 'Footer background',
  footer_text: 'Footer text',
  footer_link: 'Footer link',
  accent_color: 'Accent color',
  page_background: 'Page background',
  section_alt_background: 'Section alt background',
  border_color: 'Border color',
  preview: 'Preview',
  currency: 'Currency',
  currency_help: 'ISO currency code (e.g. NOK, USD, EUR)',
  id: 'ID',
  price: 'Price',
  category: 'Category',
  categories: 'Categories',
  categories_help: 'Define categories for better filters and grouping.',
  new_category: 'New category',
  parent_category: 'Parent category',
  parent_category_help: 'Optional. Used for grouping.',
  category_help: 'Optional. Shown in filters and used for grouping.',
  category_image_help: 'Displayed on category cards (optional).',
  title_required: 'Title is required.',
  view_all: 'View all',
  explore: 'Explore',
  view_category: 'View category',
  category_not_found: 'Category not found.',
  no_products: 'No products found',
  no_products_help: 'Try a different filter or check back later.',
  published: 'Published',
  image_url: 'Image URL',
  // Shop strings
  shop_subtitle: 'Official Plukk products, passes, and perks.',
  shop_disabled: 'Shop is disabled',
  shop_disabled_help: 'Please check back later.',
  shop_admin_help: 'Manage products for the public shop pages.',
  cart_subtitle: 'Review your items before checkout.',
  cart_empty: 'Your cart is empty',
  cart_empty_help: 'Browse the shop and add items to get started.',
  go_to_shop: 'Go to shop',
  continue_shopping: 'Continue shopping',
  qty: 'Qty',
  total: 'Total',
  back_to_shop: 'Back to shop',
  add_to_cart: 'Add to cart',
  view_cart: 'View cart',
  checkout_subtitle: 'Confirm details and place your order.',
  order_summary: 'Order summary',
  customer_details: 'Customer details',
  full_name: 'Full name',
  email: 'Email',
  phone: 'Phone',
  address: 'Address',
  back_to_cart: 'Back to cart',
  place_order: 'Place order',
  placing_order: 'Placing order...',
  order_placed: 'Order placed!',
  order_id: 'Order ID',
  login_to_checkout: 'Please log in to checkout.',
  checkout_unavailable: 'Checkout is unavailable right now.',
  checkout_missing_fields: 'Please fill name and email.',
  checkout_failed: 'Failed to place order. Please try again.',
  product_not_found: 'This product does not exist (or is unpublished).',
  not_found: 'Not found',
  // Shop (advanced)
  added_to_cart: 'Added to cart',
  browse_products: 'Browse products',
  browse_shop: 'Browse shop',
  search_products: 'Search products...',
  sort: 'Sort',
  featured: 'Featured',
  all: 'All',
  items: 'Items',
  active_campaigns: 'Active campaigns',
  deals: 'Deals',
  savings: 'Savings',
  you_saved: 'You saved',
  banners: 'Banners',
  campaigns: 'Campaigns',
  starts_at: 'Starts at',
  ends_at: 'Ends at',
  subtitle: 'Subtitle',
  variant: 'Variant',
  button_text: 'Button text',
  discount_percent: 'Discount %',
  product_ids: 'Product IDs',
  perks: 'Perks',
  interval: 'Interval',
  starting_bid: 'Starting bid',
  bid_increment: 'Bid increment',
  compare_at: 'Compare at',
  tags: 'Tags',
  tags_help: 'Comma separated. Example: airport, vip, business',
  type: 'Type',
  product: 'Product',
  service: 'Service',
  services: 'Services',
  out_of_stock: 'Out of stock',
  confirm_delete: 'Delete this item?',
  new_item: 'New item',
  new_product: 'New product',
  new_service: 'New service',
  // Product editor (CMS)
  basics: 'Basics',
  media: 'Media',
  pricing: 'Pricing + stock',
  seo: 'SEO',
  publishing: 'Publishing',
  id_help: 'Used in URLs. Leave empty to auto-generate from the title.',
  thumbnail_image: 'Thumbnail image',
  gallery_images: 'Gallery images',
  no_gallery_images: 'No gallery images yet.',
  video_url: 'Video URL',
  video_url_help: 'YouTube/Vimeo link (optional).',
  video_thumbnail: 'Video thumbnail',
  sku: 'SKU',
  barcode: 'Barcode',
  minimum_purchase_qty: 'Minimum purchase qty',
  quantity: 'Quantity',
  service_no_stock: 'Services do not require stock.',
  compare_at_help: 'Optional. Used for showing savings.',
  external_link: 'External link',
  external_link_help: 'Optional: send customers to another checkout URL.',
  external_link_button_text: 'External link button text',
  // Subscriptions
  subscriptions: 'Subscriptions',
  subscriptions_subtitle: 'Choose a plan and unlock member-only perks.',
  subscribe: 'Subscribe',
  no_subscriptions: 'No subscriptions yet',
  no_subscriptions_help: 'Ask an admin to add plans in Storefront > Shop > Subscriptions.',
  subscription_activated: 'Subscription activated.',
  subscription_failed: 'Failed to activate subscription. Please try again.',
  subscription_canceled: 'Subscription canceled.',
  subscription_cancel_failed: 'Failed to cancel subscription. Please try again.',
  popular: 'Member',
  active: 'Active',
  canceled: 'Canceled',
  // Auctions
  auctions: 'Auctions',
  auctions_subtitle: 'Bid on limited drops and special releases.',
  no_auctions: 'No auctions yet',
  no_auctions_help: 'Ask an admin to publish auctions in Storefront > Shop > Auctions.',
  auction_not_found: 'Auction not found.',
  auction_not_live: 'This auction is not live.',
  auction_upcoming: 'This auction has not started yet.',
  auction_ended: 'This auction has ended.',
  auction_unpublished: 'This auction is not published.',
  unpublished: 'Unpublished',
  upcoming: 'Upcoming',
  ended: 'Ended',
  current_bid: 'Current bid',
  bids: 'Bids',
  starts: 'Starts',
  ends: 'Ends',
  starts_in: 'Starts in',
  ends_in: 'Ends in',
  place_bid: 'Place bid',
  placing_bid: 'Placing bid...',
  your_bid: 'Your bid',
  min_bid: 'Minimum bid',
  increment: 'Increment',
  enter_valid_bid: 'Enter a valid bid amount.',
  bid_too_low: 'Bid too low (or someone outbid you). Please try again.',
  bid_failed: 'Failed to place bid. Please try again.',
  recent_bids: 'Recent bids',
  no_bids_yet: 'No bids yet. Be the first.',
  previous: 'Previous',
  // Storefront / Homepage
  select_homepage: 'Select Homepage',
  homepage_content: 'Homepage Content',
  homepage_content_help: 'Edit the main homepage copy (multi-language).',
  configure_page_layout: 'Configure your page layout',
  homepage_layout_help: 'Each homepage has a different layout. Choose one to apply on the main domain.',
  view: 'View',
  updated: 'Updated',
  homepage_1_classic: 'Homepage 1 - Classic',
  homepage_2_metro: 'Homepage 2 - Metro',
  homepage_3_minima: 'Homepage 3 - Minima',
  homepage_4_megamart: 'Homepage 4 - Megamart',
  homepage_5_reclassic: 'Homepage 5 - Re-Classic',
  homepage_6_thecore: 'Homepage 6 - The-Core',
  // Login
  signIn: 'Sign in',
  enter_email_or_mobile: 'Enter your phone number or email',
  contact_placeholder: 'Phone number or email',
  we_will_send_otp: "We will send you a verification code",
  continue: 'Continue',
  resend_otp: 'Resend code',
  resend_in: 'Resend in',
  enter_code: 'Enter the code',
  code_sent_to: "We sent a code to",
  verify: 'Verify',
  back: 'Back',
  signin_with_vipps: 'Continue with Vipps',
  signin_with_google: 'Continue with Google',
  signin_with_facebook: 'Continue with Facebook',
  use_password_login: 'Sign in with password instead',
  password_login_description: 'Sign in with your email and password',
  email_placeholder: 'Email address',
  password: 'Password',
  create_account: 'Create account',
  forgot_password: 'Forgot password?',
  use_otp_login: 'Sign in with OTP instead',
  sign_up: 'Sign up',
  signup_description: 'Sign up to get started',
  first_name: 'First name',
  last_name: 'Last name',
  or: 'or',
  // Dialogs
  alert: 'Alert',
  ok: 'OK',
  cancel: 'Cancel',
  not_logged_in: 'Not logged in',
  // Common
  quick_links: 'Quick Links',
  follow_us: 'Follow Us',
  choose_car: 'Choose car',
  next: 'Next',
  book_your_ride_menu: 'Book a ride',
  partners: 'Partners',
  partner_pages: 'Partner Pages',
  partner_slug: 'Partner ID (slug)',
  partner_name: 'Partner name',
  fleetadmin_uid: 'Fleet admin UID',
  subdomain: 'Subdomain',
  domains: 'Domains',
  partner_vanity: 'Short link path',
  partner_vanity_help: 'Optional path for sharing links.',
  custom_url: 'Custom URL',
  custom_url_help: 'Optional custom URL for QR codes and sharing.',
  partner_maintenance_mode: 'Maintenance mode',
  partner_maintenance_message: 'Maintenance message',
  access_control: 'Access control',
  access_control_subtitle: 'Define roles, permissions, and data visibility for partners and teams.',
  role_permissions: 'Role permissions',
  user_access: 'User access',
  manage_role_permissions: 'Toggle read, create, update, and delete access.',
  permissions: 'Permissions',
  perm_read: 'Read',
  perm_create: 'Create',
  perm_update: 'Update',
  perm_delete: 'Delete',
  reset_defaults: 'Reset to defaults',
  save_changes: 'Save changes',
  data_scope: 'Data scope',
  scope_all: 'All data',
  scope_partner: 'Partner data',
  scope_fleet: 'Fleet data',
  scope_self: 'Own data only',
  allowed_partners: 'Allowed partners',
  allowed_fleets: 'Allowed fleets',
  custom_permissions: 'Custom permissions',
  custom: 'Custom',
  edit_permissions: 'Edit permissions',
  no_users: 'No users found',
  no_access: 'No access',
  no_access_partners: 'You do not have permission to view partner pages.',
  website_studio: 'Website Studio',
  website_studio_subtitle: 'Update the landing experience, branding, and navigation.',
  content_language: 'Content language',
  template_gallery: 'Template gallery',
  template_gallery_subtitle: 'Pick a layout style for the landing experience.',
  jump_to_section: 'Jump to section',
  preview_site: 'Preview site',
  sections: 'Sections',
  selected: 'Selected',
  branding_seo: 'Branding & SEO',
  brand_name: 'Brand name',
  brand_logo: 'Logo URL',
  favicon_url: 'Favicon URL',
  seo_title: 'SEO title',
  seo_description: 'SEO description',
  og_image: 'OpenGraph image',
  navigation_footer: 'Navigation & Footer',
  nav_ride_links: 'Ride menu links',
  nav_drive_links: 'Drive menu links',
  nav_about_links: 'About menu links',
  footer_company_links: 'Footer company links',
  footer_product_links: 'Footer product links',
  footer_legal_links: 'Footer legal links',
  homepage_sections: 'Homepage sections',
  hero_section: 'Hero section',
  hero_title_label: 'Hero title',
  hero_subtitle_label: 'Hero subtitle',
  hero_image_label: 'Hero image URL',
  primary_cta_label: 'Primary CTA',
  secondary_cta_label: 'Secondary CTA',
  map_section: 'Map section',
  map_title_label: 'Map section title',
  how_it_works_label: 'How it works',
  how_it_works_title_label: 'Section title',
  value_props_label: 'Value propositions',
  value_cards_label: 'Value cards',
  safety_section_label: 'Safety section',
  safety_title_label: 'Safety title',
  safety_description_label: 'Safety description',
  safety_link_text_label: 'Safety link text',
  driver_business_section: 'Driver & business section',
  driver_title_label: 'Driver title',
  driver_description_label: 'Driver description',
  driver_image_label: 'Driver image URL',
  driver_primary_cta_label: 'Driver primary CTA',
  driver_secondary_cta_label: 'Driver secondary CTA',
  driver_primary_link_label: 'Driver primary CTA link',
  driver_secondary_link_label: 'Driver secondary CTA link',
  business_title_label: 'Business title',
  business_description_label: 'Business description',
  business_link_label: 'Business link',
  business_link_text_label: 'Business link text',
  business_features_label: 'Business features',
  download_section: 'Download section',
  download_title_label: 'Download title',
  download_subtitle_label: 'Download subtitle',
  show_apple_label: 'Show Apple button',
  show_android_label: 'Show Android button',
  notification_campaigns: 'Campaigns',
  notification_templates: 'Templates',
  notification_segments: 'Segments',
  send_notification: 'Send notification',
  apply_template: 'Apply template',
  apply_segment: 'Apply segment',
  title_body_required: 'Title and message are required',
  created_at: 'Created',
  team_members: 'Team members',
  load_defaults: 'Load defaults',
  label: 'Label',
  path: 'Path',
  title: 'Title',
  link: 'Link',
  image: 'Image URL',
  visible: 'Visible',
  icon: 'Icon',
  description: 'Description',
  link_text: 'Link text',
  hero_driver_card: 'Show driver promo card',
  hero_driver_title: 'Driver card title',
  hero_driver_subtitle: 'Driver card subtitle',
  roles: 'Roles',
  partner_maintenance_help: 'Shown to customers when maintenance is enabled.',
  partner_coming_soon_mode: 'Coming soon mode',
  partner_coming_soon_message: 'Coming soon message',
  partner_coming_soon_help: 'Shown to customers when coming soon is enabled.',
  preview_as_customer: 'Preview as customer',
  partner_logo_url: 'Logo URL',
  partner_favicon_url: 'Favicon URL',
  partner_page_title: 'Page title',
  partner_meta_description: 'Meta description',
  partner_og_image: 'Social share image',
  partner_support_email: 'Support email',
  partner_support_phone: 'Support phone',
  partner_support_address: 'Support address',
  landing_templates: 'Landing templates',
  landing_templates_subtitle: 'Choose a layout and palette tailored to your partner.',
  admin_theme: 'Admin theme',
  admin_theme_subtitle: 'Customize the back-office look & feel.',
  coming_soon_label: 'Coming soon',
  coming_soon_title: '{{partner}} is coming soon',
  coming_soon_description: 'We are preparing the experience. Please check back soon.',
  contact_support: 'Contact support',
  email_us: 'Email us',
  call_us: 'Call us',
  maintenance_active_title: 'Maintenance',
  maintenance_active_description: 'We are performing scheduled maintenance. Please check back soon.',
  maintenance_contact_support: 'Contact support if you need help.',
  login: 'Login',
  domain_mode: 'Domain mode',
  domain_mode_subdomain: 'Subdomain on main domain',
  domain_mode_standalone: 'Standalone domain',
  domain_mode_both: 'Both',
  domain_mode_help: 'Choose subdomain, standalone domain, or both.',
  domain_mode_standalone_help: 'Standalone domains, comma separated.',
  logo_url: 'Logo URL',
  hero_image: 'Hero image',
  cta_label: 'Button text',
  map_center_lat: 'Map center lat',
  map_center_lng: 'Map center lng',
  theme_header: 'Theme header',
  theme_header_text: 'Theme header text',
  theme_background: 'Theme background',
  saving: 'Saving',
  partner_slug_required: 'Partner ID (slug) is required',
  partner_slug_exists: 'Partner ID (slug) already exists',
  widget_preset: 'Widget preset',
  widgets_csv: 'Widgets (CSV)',
  theme_json: 'Theme settings (JSON)',
  theme_json_invalid: 'Theme settings (JSON) are invalid',
  schedule_pickup: 'Schedule pickup up to 90 days in advance',
  extra_wait_time: 'Extra wait time included to meet your trip',
  cancel_policy: 'Free cancellation up to 60 minutes in advance',
  // Hero (fallbacks)
  book_your_title: 'Your ride, right when you need it',
  about_us_content2: 'Friendly, reliable rides for people and packages.',
  hero_title: 'Go anywhere with Plukk',
  earn_on_your_terms: 'Earn on your terms',
  drivers_near_you: 'Drivers near you',
  airport_queue_title: 'Airport queue',
  airport_queue_total_cars: 'Total cars',
  airport_queue_plukk: 'Plukk',
  airport_queue_uber: 'Uber',
  airport_queue_live: 'Live',
  airport_queue_wait: 'Est. wait',
  airport_queue_min: 'min',
  airport_queue_loading: 'Loading airport queue...',
  airport_queue_unavailable: 'Queue data unavailable',
  airport_queue_worth_it: 'Worth it',
  airport_queue_moderate: 'Moderate wait',
  airport_queue_not_recommended: 'Not recommended',
  airport_queue_unknown: 'Unknown',
  airport_queue_just_now: 'Just now',
  airport_queue_mins_ago: 'min ago',
  airport_queue_hours_ago: 'hr ago',
  airport_queue_updated: 'Updated',
  basic_1_4_seats: 'Basic 1–4 seats',
  standard_electric_vehicle_1_4: 'Standard electric vehicle for 1–4 passengers',
  // Landing additions (lean redesign)
  how_it_works: 'How it works',
  how_plukk_works: 'How Plukk works',
  hiw_subtitle: 'Book in minutes, ride with confidence',
  hiw_step1_title: 'Set pickup and destination',
  hiw_step1_desc: 'Choose when you want to go.',
  hiw_step2_title: 'Pick your ride',
  hiw_step2_desc: 'Compare ETAs, seats and prices.',
  hiw_step3_title: 'Confirm and track',
  hiw_step3_desc: 'Real‑time driver updates.',
  why_choose_us: 'Why choose us',
  vp_subtitle: 'Fast pickups, fair prices, helpful support.',
  vp_safety: 'Safety first',
  vp_safety_desc: 'Every trip comes with vetted drivers and in‑app support.',
  vp_upfront: 'Transparent pricing',
  vp_upfront_desc: 'See the fare upfront — no surprises.',
  vp_schedule: 'Scheduled pickups',
  vp_schedule_desc: 'Reserve rides in advance for peace of mind.',
  vp_support: 'Here 24/7',
  vp_support_desc: 'Friendly support if you ever need a hand.',
  ride_with_plukk: 'Ride with Plukk',
  ride_with_plukk_desc: 'Request a ride and get picked up by a nearby driver in minutes.',
  drive_with_plukk: 'Drive with Plukk',
  drive_with_plukk_desc: 'Set your own hours and earn money on your schedule.',
  get_a_ride: 'Get a ride',
  become_a_driver: 'Become a driver',
  safety_first: 'Safety is a top priority',
  safety_desc: 'Whether you’re in the back seat or behind the wheel, your safety matters. We pair people with tech to keep trips safer.',
  learn_about_safety: 'Learn about safety',
  driver_screening: 'Driver screening',
  driver_screening_desc: 'Background checks before driving',
  real_time_support: 'Real-time support',
  real_time_support_desc: '24/7 customer service',
  trip_tracking: 'Trip tracking',
  trip_tracking_desc: 'Share your ride status with family',
  friendly_driver: 'Friendly driver',
  // Section fallbacks
  pruduct_section_heading_1: 'Always available',
  product_section_1: 'Request a ride, day or night.',
  pruduct_section_heading_2: 'On time',
  product_section_2: 'Reliable pickups when you need them.',
  pruduct_section_heading_3: 'Insured & vetted',
  product_section_3: 'Peace of mind on every trip.',
  pruduct_section_heading_4: 'Trusted drivers',
  product_section_4: 'Local experts behind the wheel.',
  best_service_provider: 'Your trusted ride partner',
  service_start_soon: 'Launching in your city soon',
  // Uber-like driver promo
  drive_when_you_want: 'Drive when you want',
  drive_subtitle: 'You decide when to go online. Earn on your schedule.',
  make_money_when_you_need: 'Make money when you need',
  money_subtitle: 'Flexible earning with upfront fares and in‑app support.',
  grow_taxi_business: 'Grow your taxi business',
  taxi_subtitle: 'Add vehicles & drivers, track performance, and earn more.',
  driver: 'Driver',
  get_started: 'Get started',
  learn_more: 'Learn more',
  become_partner: 'Become a partner',
  flexible_hours: 'Flexible hours',
  flexible_hours_desc: 'Choose when you drive and for how long.',
  upfront_fares: 'Upfront fares',
  upfront_fares_desc: 'See the price before you accept a trip.',
  add_vehicles: 'Add vehicles & drivers',
  fleet_dashboard: 'Fleet dashboard',
  driver_signup_subtitle: 'Drive when you want, earn what you need. Set your own hours and keep more of what you earn.',
  plukk_for_business: 'Plukk for Business',
  business_desc: 'Transform the way your company moves. Simple travel management for businesses of all sizes.',
  simplified_billing: 'Simplified billing',
  simplified_billing_desc: 'One monthly invoice for all rides',
  central_dashboard: 'Central dashboard',
  central_dashboard_desc: 'Track rides and manage users',
  expense_integration: 'Expense integration',
  expense_integration_desc: 'Works with your expense tools',
  // Profile quick actions
  quick_actions: 'Quick actions',
  book_a_ride: 'Book a ride',
  my_bookings: 'My bookings',
  wallet: 'Wallet',
  my_cars: 'My cars',
  riders: 'Riders',
  drivers: 'Drivers',
  fleetadmins: 'Fleet admins',
  alladmin: 'Admins',
  dashboard: 'Dashboard',
  book_now: 'Book now',
  min_fare: 'Minimum fare',
  extra_info: 'Extra info',
  mile: 'mile',
  km: 'km',
  // Header/Footer fallbacks
  home: 'Home',
  myaccount: 'My account',
  login_signup: 'Login / Sign up',
  about_us: 'About us',
  contact_us: 'Contact us',
  privacy_policy: 'Privacy policy',
  security: 'Security',
  privacy_data: 'Privacy & data',
  privacy_blurb: 'We collect the minimum info needed to run your account and trips. You can update your details anytime or contact support to review, export, or delete your data.',
  // Download section
  download_apps_title: 'Download the apps and go',
  download_apps_subtitle: 'Available for iOS and Android. Get the app that fits your needs.',
  mobile_apps_on_store: 'Mobile Apps available on App Stores',
  app_store_deception1: 'Download the free app to request a ride or drive and earn money on your schedule.',
  download_brand_title: 'Get the ride you want',
  download_brand_body: 'PLUKK is always available. Whether you are heading to your usual destination or exploring somewhere new, open the PLUKK app and a nearby driver will take you there. Download and get started.',
  app_store_cta: 'Download on the App Store',
  play_store_cta: 'Get it on Google Play',
  download_on: 'Download on the',
  get_it_on: 'Get it on',
  // QuickBook
  quick_book: 'Quick book',
  pickup_location: 'Pickup location',
  drop_location: 'Dropoff location',
  start_booking: 'Start booking',
  drive_with_us: 'Drive with us',
  loading_map: 'Loading map...',
  nearest_driver: 'Nearest driver',
  drivers_nearby: 'drivers nearby',
  live: 'Live',
  // Stepper labels
  step_locations: 'Locations',
  step_ride: 'Ride',
  step_confirm: 'Confirm',
  why_choose_plukk: 'Why choose Plukk Taxi',
  vp_business: 'Business accounts',
  vp_business_desc: 'Monthly billing and trip reports for teams.',
  vp_green: 'Green options',
  vp_green_desc: 'Choose low‑emission vehicles when available.',
  // Admin assign on AddBookings
  assign_driver_optional: 'Assign to driver (optional)',
  no_preference: 'No preference',
  // Edit booking additions
  edit_booking: 'Edit booking',
  contact_name: 'Contact name',
  contact_phone: 'Contact phone',
  contact_email: 'Contact email',
  note_to_driver: 'Note to driver',
  save: 'Save'
  ,
  // Manual assign extras
  force_accept_now: 'Force accept now',
  yes: 'Yes',
  no: 'No',
  driver_assigned: 'A driver has been assigned to your trip',
  driver_assigned_success: 'Driver assigned successfully'
  ,
  offline: 'offline'
  ,
  // Dispatch settings labels
  dispatch_settings: 'Dispatch settings',
  pre_dispatch_window_mins: 'Pre-dispatch window (mins)',
  request_timeout_sec: 'Request timeout (sec)',
  dispatch_mode: 'Dispatch mode',
  mode_nearest: 'Nearest',
  mode_online_longest: 'Online longest',
  mode_hybrid: 'Nearest + longest online'
  ,
  pending_offers: 'Pending offers',
  next_offer_in: 'Next offer in',
  manage: 'Manage'
  ,
  test_dispatch: 'Test dispatch',
  booking_id: 'Booking ID',
  run: 'Run'
  ,
  zone_dispatch_mode: 'Zone dispatch mode',
  default: 'Default'
  ,
  drivers_by_zone: 'Drivers by zone (online)',
  more: 'more',
  zone_polygon_error: 'Please draw a polygon with at least 3 points.'
  ,
  // Bookings page helpers
  active_trip: 'Active trip',
  active_trip_hint: 'You have a trip in progress. Tap to view details.',
  view_details: 'View details',
  no_bookings_yet: 'No bookings yet',
  no_bookings_help: 'Start by creating your first booking.'
  ,
  // Zone widgets
  car_types: 'Car types',
  security_info: 'You can change your email or phone in Personal info. For your security, we verify changes with a one‑time code (OTP).',
  tip_strong_password: "Tip: Use a strong password on your mobile app account and don't share one-time codes with anyone.",
  apply_language: 'Update language',
  get_there: 'Get there',
  avg_pickup_time: 'Avg. pickup time',
  available: 'Available',
  active_drivers: 'Active drivers',
  simple_process: 'Simple process',
  why_us: 'Why choose us',
  available_now: 'Available now',
  free_download: 'Free download',
  download_app: 'Download app',
  weekly_payouts: 'Weekly payouts',
  detailed_analytics: 'Detailed analytics'
  ,
  currency_settings: 'Currency settings',
  currency_symbol: 'Currency symbol',
  currency_code: 'Currency code',
  set_decimal: 'Set decimal',
  swipe_symbol_direction: 'Swipe symbol direction',
  disable_online_payments: 'Disable online payments',
  disable_cash_payments: 'Disable cash payments',
  maintenance_settings: 'Maintenance settings',
  maintenance_mode: 'Maintenance mode',
  maintenance_message: 'Maintenance message',
  security_settings: 'Security settings',
  booking_otp: 'Booking OTP',
  driver_approval: 'Driver approval',
  car_approval: 'Car approval',
  verify_id_password: 'Verify ID / password',
  referral_bonus: 'Referral bonus',
  wallet_denominations: 'Wallet denominations',
  advance_settings: 'Advance settings',
  select_country: 'Select country',
  autocomplete_country_restriction: 'Autocomplete country restriction',
  allow_multi_country_selection: 'Allow multi-country selection',
  convert_to_mile: 'Convert to mile',
  repeat_sound_on_new_trip: 'Repeat sound on new trip',
  allow_delivery_pick_image: 'Allow delivery pick image',
  allow_delivery_drop_image: 'Allow delivery drop image',
  customer_withdraw: 'Customer withdraw',
  car_list_view_horizontal: 'Car list view horizontal',
  use_distance_matrix_api: 'Use Distance Matrix API',
  driver_settings: 'Driver settings',
  driver_radius: 'Driver radius',
  auto_dispatch: 'Auto dispatch',
  allow_driver_negative_balance: 'Allow driver negative balance',
  bank_reg_fields: 'Bank registration fields',
  show_live_route: 'Show live route'
  ,
  // AddBookings / request ride
  request_ride: 'Request a ride',
  pickup_now: 'Pickup now',
  schedule: 'Schedule for later',
  suggestions: 'Suggestions',
  select_user: 'Select user',
  enter_pickup: 'Enter pickup',
  enter_destination: 'Enter destination',
  see_prices: 'See prices',
  add_phone: 'Add phone number',
  add_email: 'Add email',
  confirm_and_request: 'Confirm and request',
  payment_mode: 'Payment mode',
  bookingPayment: 'Payment',
  wallet_balance_low: 'Wallet balance too low',
  wallet_booking_alert: 'Use wallet balance for this booking?',
  use_wallet_balance: 'Use wallet balance',
  paynow_button: 'Pay now',
  add_pickup_note: 'Add pickup note',
  ride_options: 'Ride options',
  parcel_types: 'Parcel types',
  options: 'Options',
  confirm_ride: 'Confirm ride',
  confirm_pickup_location: 'Confirm pickup location',
  distance: 'Distance',
  time: 'Time',
  fare: 'Fare',
  faster: 'Faster',
  personal: 'Personal',
  enter_destination_hint: 'Add drop-off to see price',
  request: 'Request',
  select_proper: 'Please select all required fields',
  place_to_coords_error: 'Could not locate that address',
  // Driver Earning & Finance Reports
  driver_earning: 'Driver Earnings',
  driver_earning_description: 'View and track driver earnings by period',
  earning_reports: 'Earning Reports',
  earning_reports_description: 'Monthly revenue and profit analysis',
  generated_on: 'Generated on',
  total_earnings: 'Total Earnings',
  total_rides: 'Total Rides',
  avg_earning: 'Avg Earning',
  export: 'Export',
  export_csv: 'Export CSV',
  export_pdf: 'Export PDF',
  results: 'results',
  records_found: 'records found',
  driver_info: 'Driver Info',
  earning_details: 'Earning Details',
  earning_amount: 'Earnings',
  period: 'Period',
  period_info: 'Period Info',
  vehicle: 'Vehicle',
  month: 'Month',
  year: 'Year',
  all_years: 'All Years',
  all_months: 'All Months',
  clear: 'Clear',
  clear_all: 'Clear All',
  filters: 'Filters',
  Gross_trip_cost: 'Gross Cost',
  Discounts: 'Discounts',
  Profit: 'Profit',
  trip_cost: 'Trip Cost',
  trip_cost_driver_share: 'Driver Share',
  total_bookings: 'Total Bookings',
  gross_revenue: 'Gross Revenue',
  total_profit: 'Total Profit',
  total_discounts: 'Total Discounts',
  revenue_details: 'Revenue Details',
  profit_details: 'Profit Details',
  net_profit: 'Net Profit',
  total_records: 'Total Records',
  // Shift Data & Taximeter
  shift_data: 'Shift Data',
  list_shifts: 'List Shifts',
  taxi_owners: 'Taxi Owners',
  taxi_information: 'Taxi Information',
  income_shift_report: 'Income/Shift Report',
  shift_id: 'Shift ID',
  driver_id: 'Driver ID',
  start_date: 'Start Date',
  end_date: 'End Date',
  shift_duration: 'Shift Duration',
  number_of_trips: 'Number of Trips',
  canceled_trips: 'Canceled Trips',
  total_revenue: 'Total Revenue',
  cash_revenue: 'Cash Revenue',
  credit_revenue: 'Credit Revenue',
  ttcard_revenue: 'TTCard Revenue',
  canceled_trips_revenue: 'Canceled Trips Revenue',
  total_km: 'Total KM',
  terminal_fee: 'Terminal Fee',
  filter: 'Filter',
  reset: 'Reset',
  nothing_selected: 'Nothing selected',
  no_details_error: 'Please fill in all required details',
  no_description: 'No description available',
  no_zones: 'No active zones',
  from_date: 'From Date',
  to_date: 'To Date',
  driver_all: 'Driver (All)',
  owner: 'Owner',
  taxi_id: 'Taxi ID',
  license_plate: 'License Plate',
  taxi_model: 'Taxi Model',
  owner_name: 'Owner Name',
  owner_phone: 'Owner Phone',
  owner_email: 'Owner Email',
  company_name: 'Company Name',
  organization_number: 'Organization Number',
  total_vehicles: 'Total Vehicles',
  active_vehicles: 'Active Vehicles',
  // Cars & Vehicles
  vehicle_details: 'Vehicle Details',
  active_status: 'Active Status',
  choose_image_first: 'Please choose an image first',
  car_image: 'Car Image',
  proper_input_name: 'Please enter a valid name',
  // Shift Data Pages
  taxi_owners_description: 'Manage taxi owners and their fleets',
  taxi_information_description: 'Manage vehicle details and taximeter information',
  income_shift_report_description: 'View shift data and income reports by driver and vehicle',
  add_owner: 'Add Owner',
  add_vehicle: 'Add Vehicle',
  total_owners: 'Total Owners',
  meter_number: 'Meter Number',
  last_inspection: 'Last Inspection',
  maintenance: 'Maintenance',
  shift_date: 'Date',
  shift_time: 'Shift Time',
  gross_income: 'Gross',
  net_income: 'Net',
  shifts: 'Shifts',
  total_hours: 'Hours',
  gross: 'Gross',
  net: 'Net',
  all_status: 'All Status',
  trips: 'Trips',
  // Language Menu
  language_updated: 'Language updated to',
  select_language: 'Select Language',
};

const defaultNb = {
  // Navigation
  ride: 'Kjør',
  drive: 'Kjør selv',
  storefront: 'Storefront',
  storefront_subtitle: 'Tilpass hovedstartside for primærdomenet.',
  about: 'Om',
  company: 'Selskap',
  products: 'Produkter',
  dark: 'Mørk',
  light: 'Lys',
  dark_mode: 'Mørk modus',
  transparent: 'Gjennomsiktig',
  admin_theme: 'Admin-tema',
  admin_theme_subtitle: 'Tilpass utseendet i administrasjonen.',
  category: 'Kategori',
  categories: 'Kategorier',
  categories_help: 'Definer kategorier for bedre filtre og gruppering.',
  new_category: 'Ny kategori',
  parent_category: 'Overkategori',
  parent_category_help: 'Valgfritt. Brukes for gruppering.',
  category_help: 'Valgfritt. Vises i filtre og brukes for gruppering.',
  category_image_help: 'Vises på kategorikort (valgfritt).',
  title_required: 'Tittel er påkrevd.',
  view_all: 'Se alle',
  explore: 'Utforsk',
  view_category: 'Se kategori',
  category_not_found: 'Kategori ikke funnet.',
  no_products: 'Ingen produkter funnet',
  no_products_help: 'Prøv et annet filter eller kom tilbake senere.',
  // Shop strings
  shop: 'Butikk',
  cart: 'Handlekurv',
  checkout: 'Betaling',
  shop_subtitle: 'Offisielle Plukk-produkter, pass og fordeler.',
  shop_disabled: 'Butikken er deaktivert',
  shop_disabled_help: 'Kom tilbake senere.',
  shop_admin_help: 'Administrer produkter for offentlige butikksider.',
  cart_subtitle: 'Se over varene dine før betaling.',
  cart_empty: 'Handlekurven din er tom',
  cart_empty_help: 'Bla i butikken og legg til varer for å komme i gang.',
  go_to_shop: 'Gå til butikk',
  continue_shopping: 'Fortsett å handle',
  qty: 'Antall',
  total: 'Totalt',
  back_to_shop: 'Tilbake til butikken',
  add_to_cart: 'Legg i handlekurv',
  view_cart: 'Se handlekurv',
  checkout_subtitle: 'Bekreft detaljer og legg inn bestilling.',
  order_summary: 'Bestillingssammendrag',
  customer_details: 'Kundedetaljer',
  full_name: 'Fullt navn',
  email: 'E-post',
  phone: 'Telefon',
  address: 'Adresse',
  back_to_cart: 'Tilbake til handlekurv',
  place_order: 'Legg inn bestilling',
  placing_order: 'Legger inn bestilling...',
  order_placed: 'Bestilling lagt inn!',
  order_id: 'Bestillings-ID',
  login_to_checkout: 'Logg inn for å betale.',
  checkout_unavailable: 'Betaling er ikke tilgjengelig akkurat nå.',
  checkout_missing_fields: 'Fyll inn navn og e-post.',
  checkout_failed: 'Kunne ikke legge inn bestilling. Prøv igjen.',
  product_not_found: 'Dette produktet finnes ikke (eller er ikke publisert).',
  not_found: 'Ikke funnet',
  // Shop (advanced)
  added_to_cart: 'Lagt i handlekurven',
  browse_products: 'Se produkter',
  browse_shop: 'Se butikk',
  search_products: 'Søk produkter...',
  sort: 'Sorter',
  featured: 'Anbefalt',
  all: 'Alle',
  items: 'Varer',
  active_campaigns: 'Aktive kampanjer',
  deals: 'Tilbud',
  savings: 'Spar',
  you_saved: 'Du sparte',
  banners: 'Bannere',
  campaigns: 'Kampanjer',
  starts_at: 'Starter',
  ends_at: 'Slutter',
  subtitle: 'Undertittel',
  variant: 'Variant',
  button_text: 'Knappetekst',
  discount_percent: 'Rabatt %',
  product_ids: 'Produkt-IDer',
  perks: 'Fordeler',
  interval: 'Intervall',
  starting_bid: 'Startbud',
  bid_increment: 'Budøkning',
  compare_at: 'Førpris',
  tags: 'Tagger',
  // Subscriptions
  subscriptions: 'Abonnement',
  subscriptions_subtitle: 'Velg en plan og få medlemsfordeler.',
  subscribe: 'Abonner',
  no_subscriptions: 'Ingen abonnement ennå',
  no_subscriptions_help: 'Be en admin legge til planer i Storefront > Shop > Subscriptions.',
  subscription_activated: 'Abonnement aktivert.',
  subscription_failed: 'Kunne ikke aktivere abonnement. Prøv igjen.',
  subscription_canceled: 'Abonnement avsluttet.',
  subscription_cancel_failed: 'Kunne ikke avslutte abonnement. Prøv igjen.',
  popular: 'Medlem',
  active: 'Aktiv',
  canceled: 'Avsluttet',
  // Auctions
  auctions: 'Auksjoner',
  auctions_subtitle: 'By på begrensede slipp og spesielle utgivelser.',
  no_auctions: 'Ingen auksjoner ennå',
  no_auctions_help: 'Be en admin publisere auksjoner i Storefront > Shop > Auctions.',
  auction_not_found: 'Auksjon ikke funnet.',
  auction_not_live: 'Denne auksjonen er ikke live.',
  auction_upcoming: 'Denne auksjonen har ikke startet ennå.',
  auction_ended: 'Denne auksjonen er avsluttet.',
  auction_unpublished: 'Denne auksjonen er ikke publisert.',
  unpublished: 'Ikke publisert',
  upcoming: 'Kommende',
  ended: 'Avsluttet',
  current_bid: 'Nåværende bud',
  bids: 'Bud',
  starts: 'Starter',
  ends: 'Slutter',
  starts_in: 'Starter om',
  ends_in: 'Slutter om',
  place_bid: 'Legg inn bud',
  placing_bid: 'Legger inn bud...',
  your_bid: 'Ditt bud',
  min_bid: 'Minimumsbud',
  increment: 'Økning',
  enter_valid_bid: 'Oppgi et gyldig budbeløp.',
  bid_too_low: 'Budet er for lavt (eller noen overbød deg). Prøv igjen.',
  bid_failed: 'Kunne ikke legge inn bud. Prøv igjen.',
  recent_bids: 'Siste bud',
  no_bids_yet: 'Ingen bud ennå. Bli den første.',
  previous: 'Forrige',
  business: 'Bedrift',
  privacy: 'Personvern',
  terms: 'Vilkår',
  language: 'Språk',
  // Storefront / Homepage
  select_homepage: 'Velg startside',
  homepage_content: 'Innhold på startsiden',
  homepage_content_help: 'Rediger hovedsidens innhold (flerspråklig).',
  configure_page_layout: 'Konfigurer sideoppsettet',
  homepage_layout_help: 'Hver startside har et eget oppsett. Velg ett for hoveddomenet.',
  view: 'Vis',
  updated: 'Oppdatert',
  homepage_1_classic: 'Startside 1 - Klassisk',
  homepage_2_metro: 'Startside 2 - Metro',
  homepage_3_minima: 'Startside 3 - Minimal',
  homepage_4_megamart: 'Startside 4 - Megamart',
  homepage_5_reclassic: 'Startside 5 - Re-klassisk',
  homepage_6_thecore: 'Startside 6 - The-Core',
  basic_1_4_seats: 'Grunnleggende 1–4 seter',
  standard_electric_vehicle_1_4: 'Standard elektrisk kjøretøy for 1–4 passasjerer',
  airport_queue_title: 'Flyplasskø',
  airport_queue_total_cars: 'Totalt antall biler',
  airport_queue_plukk: 'Plukk',
  airport_queue_uber: 'Uber',
  airport_queue_live: 'Live',
  airport_queue_wait: 'Estimert ventetid',
  airport_queue_min: 'min',
  airport_queue_loading: 'Laster flyplasskø...',
  airport_queue_unavailable: 'Kødata utilgjengelig',
  airport_queue_worth_it: 'Verdt det!',
  airport_queue_moderate: 'Moderat ventetid',
  airport_queue_not_recommended: 'Ikke anbefalt',
  airport_queue_unknown: 'Ukjent',
  airport_queue_just_now: 'Akkurat nå',
  airport_queue_mins_ago: 'min siden',
  airport_queue_hours_ago: 't siden',
  airport_queue_updated: 'Oppdatert',
  // Login
  signIn: 'Logg inn',
  enter_email_or_mobile: 'Skriv inn telefonnummer eller e-post',
  contact_placeholder: 'Telefonnummer eller e-post',
  we_will_send_otp: 'Vi sender deg en bekreftelseskode',
  continue: 'Fortsett',
  resend_otp: 'Send kode på nytt',
  resend_in: 'Send på nytt om',
  enter_code: 'Skriv inn kode',
  code_sent_to: 'Vi har sendt en kode til',
  verify: 'Bekreft',
  back: 'Tilbake',
  signin_with_vipps: 'Fortsett med Vipps',
  signin_with_google: 'Fortsett med Google',
  signin_with_facebook: 'Fortsett med Facebook',
  use_password_login: 'Logg inn med passord i stedet',
  password_login_description: 'Logg inn med e-post og passord',
  email_placeholder: 'E-postadresse',
  password: 'Passord',
  create_account: 'Opprett konto',
  forgot_password: 'Glemt passord?',
  use_otp_login: 'Logg inn med engangskode i stedet',
  sign_up: 'Registrer deg',
  signup_description: 'Registrer deg for å komme i gang',
  first_name: 'Fornavn',
  last_name: 'Etternavn',
  or: 'eller',
  // Dialogs
  alert: 'Varsel',
  ok: 'OK',
  cancel: 'Avbryt',
  not_logged_in: 'Du er ikke logget inn',
  login: 'Logg inn',
  // Common
  home: 'Startside',
  my_bookings: 'Bestillingshistorikk',
  wallet: 'Lommebok',
  book_a_ride: 'Bestill en tur',
  riders: 'Passasjerer',
  drivers: 'Sjåfører',
  coming_soon_label: 'Kommer snart',
  coming_soon_title: '{{partner}} kommer snart',
  coming_soon_description: 'Vi forbereder opplevelsen. Kom tilbake snart.',
  contact_support: 'Kontakt support',
  email_us: 'Send e-post',
  call_us: 'Ring oss',
  maintenance_active_title: 'Vedlikehold',
  maintenance_active_description: 'Vi utfører planlagt vedlikehold. Kom tilbake snart.',
  maintenance_contact_support: 'Kontakt support hvis du trenger hjelp.',
  partner_coming_soon_mode: 'Kommer snart-modus',
  partner_coming_soon_message: 'Kommer snart-melding',
  partner_coming_soon_help: 'Vises til kunder når kommer snart er aktivert.',
  fleetadmins: 'Flåteadministratorer',
  alladmin: 'Administratorer',
  drive_with_us: 'Kjør med oss',
  loading_map: 'Laster inn kart...',
  nearest_driver: 'Nærmeste sjåfør',
  drivers_nearby: 'sjåfører i nærheten',
  live: 'Live',
  why_choose_plukk: 'Grunner til å velge Plukk Taxi',
  vp_subtitle: 'Rask henting, rettferdig pris, og god support.',
  vp_safety: 'Trygghet først',
  vp_safety_desc: 'Godkjente sjåfører og support i appen.',
  vp_upfront: 'Forutsigbar pris',
  vp_upfront_desc: 'Se prisen før du reiser.',
  vp_schedule: 'Planlagte hentinger',
  vp_schedule_desc: 'Bestill på forhånd.',
  vp_support: 'Support døgnet rundt',
  vp_support_desc: 'Vi er her hvis du trenger hjelp.',
  vp_business: 'Bedriftskontoer',
  vp_business_desc: 'Månedsfakturering og rapporter for team.',
  vp_green: 'Grønne valg',
  vp_green_desc: 'Velg lavutslippsbiler når det er mulig.',
  driver: 'Sjåfør',
  book_your_title: 'Hvor skal vi ta deg i dag?',
  about_us_content2: 'Administrer bestillinger, be om tilbud eller bestill en tjeneste med vårt enkle og raske system. Vi er et on-demand-selskap som lar gjester bestille tjenester på nett.',
  hero_title: 'Reis hvor som helst med Plukk',
  earn_on_your_terms: 'Tjen på dine premisser',
  drivers_near_you: 'Sjåfører i nærheten',
  schedule_pickup: 'Planlegg henting opptil 90 dager i forveien',
  extra_wait_time: 'Ekstra ventetid inkludert for å møte turen din',
  cancel_policy: 'Gratis avbestilling opptil 60 minutter før',
  step_locations: 'Steder',
  step_ride: 'Reise',
  step_confirm: 'Bekreft',
  security: 'Sikkerhet',
  privacy_data: 'Personvern og data',
  apply_language: 'Oppdater språk',
  get_there: 'Kom deg frem',
  avg_pickup_time: 'Snitt hentetid',
  available: 'Tilgjengelig',
  active_drivers: 'Aktive sjåfører',
  simple_process: 'Enkel prosess',
  why_us: 'Hvorfor velge oss',
  available_now: 'Tilgjengelig nå',
  free_download: 'Gratis nedlasting',
  download_app: 'Last ned app',
  weekly_payouts: 'Ukentlige utbetalinger',
  detailed_analytics: 'Detaljert analyse',
  mobile_apps_on_store: 'Mobilapper tilgjengelig i app-butikker',
  app_store_deception1: 'Last ned den gratis appen for å bestille tur eller kjøre og tjene penger når det passer deg.',
  download_brand_title: 'Få turen du vil ha',
  download_brand_body: 'Plukk er alltid tilgjengelig. Skal du et kjent sted eller utforsker noe nytt, åpne Plukk-appen og en sjåfør i nærheten tar deg dit. Last ned og kom i gang.',
  app_store_cta: 'Last ned i App Store',
  play_store_cta: 'Få den på Google Play',
  download_on: 'Last ned fra',
  get_it_on: 'Hent den på',
  pickup_location: 'Hentested',
  drop_location: 'Avleveringssted',
  choose_car: 'Velg bil',
  how_it_works: 'Hvordan det fungerer',
  how_plukk_works: 'Slik fungerer Plukk',
  hiw_subtitle: 'Bestill på minutter, reis med trygghet',
  hiw_step1_title: 'Sett hente- og leveringssted',
  hiw_step1_desc: 'Velg når du vil dra.',
  hiw_step2_title: 'Velg bilen',
  hiw_step2_desc: 'Sammenlign ETA, seter og priser.',
  hiw_step3_title: 'Bekreft og følg',
  hiw_step3_desc: 'Oppdateringer fra sjåføren i sanntid.',
  why_choose_us: 'Hvorfor velge oss',
  ride_with_plukk: 'Reis med Plukk',
  ride_with_plukk_desc: 'Bestill en tur og bli hentet av en sjåfør på få minutter.',
  drive_with_plukk: 'Kjør med Plukk',
  drive_with_plukk_desc: 'Sett dine egne tider og tjen penger når det passer deg.',
  get_a_ride: 'Bestill en tur',
  become_a_driver: 'Bli sjåfør',
  safety_first: 'Sikkerhet først',
  safety_desc: 'Enten du er passasjer eller sjåfør er sikkerhet viktig. Vi kombinerer folk og teknologi for tryggere turer.',
  learn_about_safety: 'Les om sikkerhet',
  driver_screening: 'Bakgrunnssjekk av sjåfører',
  driver_screening_desc: 'Bakgrunnssjekk før du kjører',
  real_time_support: 'Support i sanntid',
  real_time_support_desc: 'Kundeservice hele døgnet',
  trip_tracking: 'Sporing av tur',
  trip_tracking_desc: 'Del reisen med familie',
  friendly_driver: 'Hyggelig sjåfør',
  driver_signup_subtitle: 'Kjør når du vil, tjen det du trenger. Sett din egen plan og behold mer av det du tjener.',
  plukk_for_business: 'Plukk for bedrifter',
  business_desc: 'Gjør bedriftstransport enklere. Enkel reiseadministrasjon for alle størrelser.',
  simplified_billing: 'Forenklet fakturering',
  simplified_billing_desc: 'Én månedlig faktura for alle turer',
  central_dashboard: 'Sentraloversikt',
  central_dashboard_desc: 'Følg turer og administrer brukere',
  expense_integration: 'Utgiftsintegrasjon',
  expense_integration_desc: 'Fungerer med utgiftsverktøyene deres',
  // Admin assign on AddBookings
  assign_driver_optional: 'Tilordne sjåfør (valgfritt)',
  no_preference: 'Ingen preferanse',
  // Edit booking additions
  edit_booking: 'Rediger bestilling',
  contact_name: 'Kontakt navn',
  contact_phone: 'Kontaktnummer',
  contact_email: 'Kontakt e‑post',
  note_to_driver: 'Melding til sjåfør',
  save: 'Lagre'
  ,
  // Manual assign extras
  force_accept_now: 'Tving godkjenning nå',
  yes: 'Ja',
  no: 'Nei',
  driver_assigned: 'En sjåfør er tilordnet turen din',
  driver_assigned_success: 'Sjåfør tilordnet'
  ,
  offline: 'Frakoblet'
  ,
  // Dispatch settings labels
  dispatch_settings: 'Utkjøringsinnstillinger',
  pre_dispatch_window_mins: 'Forvarsling (minutter)',
  request_timeout_sec: 'Svarfrist (sekunder)',
  dispatch_mode: 'Fordelingsmodus',
  mode_nearest: 'Nærmest',
  mode_online_longest: 'Lengst online',
  mode_hybrid: 'Nærmest + lengst online'
  ,
  pending_offers: 'Ventende tilbud',
  next_offer_in: 'Neste tilbud om',
  manage: 'Administrer'
  ,
  test_dispatch: 'Test fordeling',
  booking_id: 'Bestillings-ID',
  run: 'Kjør'
  ,
  zone_dispatch_mode: 'Sonens fordelingsmodus',
  default: 'Standard'
  ,
  drivers_by_zone: 'Sjåfører per sone (online)',
  more: 'mer',
  zone_polygon_error: 'Tegn en polygon med minst 3 punkter.'
  ,
  // Bookings page helpers
  active_trip: 'Aktiv tur',
  active_trip_hint: 'Du har en tur i gang. Trykk for detaljer.',
  view_details: 'Se detaljer',
  no_bookings_yet: 'Ingen bestillinger ennå',
  no_bookings_help: 'Start med å lage din første bestilling.'
  ,
  // Zone widgets
  car_types: 'Biltyper'
  ,
  security_info: 'Du kan endre e-post eller telefon under Personopplysninger. Av sikkerhetshensyn bekrefter vi med engangskode (OTP).',
  tip_strong_password: 'Tips: Bruk et sterkt passord og del aldri engangskoder med andre.',
  privacy_blurb: 'Vi samler bare inn det som trengs for konto og turer. Du kan oppdatere detaljene når som helst eller kontakte support for å se, eksportere eller slette data.'
  ,
  request_ride: 'Bestill en tur',
  pickup_now: 'Hent nå',
  schedule: 'Planlegg senere',
  suggestions: 'Forslag',
  select_user: 'Velg bruker',
  enter_pickup: 'Skriv henteadresse',
  enter_destination: 'Skriv leveringsadresse',
  see_prices: 'Se priser',
  add_phone: 'Legg til telefon',
  add_email: 'Legg til e-post',
  confirm_and_request: 'Bekreft og bestill',
  payment_mode: 'Betalingsmåte',
  bookingPayment: 'Betaling',
  wallet_balance_low: 'For lav lommeboksaldo',
  wallet_booking_alert: 'Bruk lommebok til denne bestillingen?',
  use_wallet_balance: 'Bruk lommebok',
  paynow_button: 'Betal nå',
  add_pickup_note: 'Legg til melding for henting',
  ride_options: 'Alternativer',
  parcel_types: 'Pakke-typer',
  options: 'Valg',
  confirm_ride: 'Bekreft tur',
  confirm_pickup_location: 'Bekreft henteadresse',
  distance: 'Distanse',
  time: 'Tid',
  fare: 'Pris',
  faster: 'Raskere',
  personal: 'Personlig',
  enter_destination_hint: 'Legg til levering for å se pris',
  request: 'Bestill',
  select_proper: 'Fyll ut alle påkrevde felt',
  place_to_coords_error: 'Fant ikke adressen',
  // Driver Earning & Finance Reports
  driver_earning: 'Sjåførinntekt',
  driver_earning_description: 'Se og følg sjåførinntekter per periode',
  earning_reports: 'Inntektsrapporter',
  earning_reports_description: 'Månedlig inntekts- og fortjenesteanalyse',
  generated_on: 'Generert',
  total_earnings: 'Total inntekt',
  total_rides: 'Totale turer',
  avg_earning: 'Snitt inntekt',
  export: 'Eksporter',
  export_csv: 'Eksporter CSV',
  export_pdf: 'Eksporter PDF',
  results: 'resultater',
  records_found: 'poster funnet',
  driver_info: 'Sjåførinformasjon',
  earning_details: 'Inntektsdetaljer',
  earning_amount: 'Inntekt',
  period: 'Periode',
  period_info: 'Periodeinformasjon',
  vehicle: 'Kjøretøy',
  month: 'Måned',
  year: 'År',
  all_years: 'Alle år',
  all_months: 'Alle måneder',
  clear: 'Tøm',
  clear_all: 'Tøm alle',
  filters: 'Filtre',
  Gross_trip_cost: 'Bruttokostnad',
  Discounts: 'Rabatter',
  Profit: 'Fortjeneste',
  trip_cost: 'Turkostnad',
  trip_cost_driver_share: 'Sjåførandel',
  total_bookings: 'Totale bestillinger',
  gross_revenue: 'Bruttoinntekt',
  total_profit: 'Total fortjeneste',
  total_discounts: 'Totale rabatter',
  revenue_details: 'Inntektsdetaljer',
  profit_details: 'Fortjenestedetaljer',
  net_profit: 'Netto fortjeneste',
  total_records: 'Totalt antall poster',
  // Shift Data & Taximeter
  shift_data: 'Skiftdata',
  list_shifts: 'Liste over skift',
  taxi_owners: 'Drosje-eiere',
  taxi_information: 'Drosjeinformasjon',
  income_shift_report: 'Inntekt/Skiftrapport',
  shift_id: 'Skift-ID',
  driver_id: 'Sjåfør-ID',
  start_date: 'Startdato',
  end_date: 'Sluttdato',
  shift_duration: 'Skiftvarighet',
  number_of_trips: 'Antall turer',
  canceled_trips: 'Kansellerte turer',
  total_revenue: 'Total inntekt',
  cash_revenue: 'Kontantinntekt',
  credit_revenue: 'Kortinntekt',
  ttcard_revenue: 'TT-kort inntekt',
  canceled_trips_revenue: 'Kansellerte turer inntekt',
  total_km: 'Totalt KM',
  terminal_fee: 'Terminalgebyr',
  filter: 'Filtrer',
  reset: 'Tilbakestill',
  nothing_selected: 'Ingenting valgt',
  no_details_error: 'Fyll inn alle påkrevde felter',
  no_description: 'Ingen beskrivelse tilgjengelig',
  no_zones: 'Ingen aktive soner',
  from_date: 'Fra dato',
  to_date: 'Til dato',
  driver_all: 'Sjåfør (Alle)',
  owner: 'Eier',
  taxi_id: 'Drosje-ID',
  license_plate: 'Registreringsnummer',
  taxi_model: 'Drosjemodell',
  owner_name: 'Eiernavn',
  owner_phone: 'Eiertelefon',
  owner_email: 'Eier e-post',
  company_name: 'Firmanavn',
  organization_number: 'Organisasjonsnummer',
  total_vehicles: 'Totalt antall kjøretøy',
  active_vehicles: 'Aktive kjøretøy',
  // Cars & Vehicles
  vehicle_details: 'Kjøretøydetaljer',
  active_status: 'Aktiv status',
  choose_image_first: 'Velg et bilde først',
  car_image: 'Bilbilde',
  proper_input_name: 'Skriv inn et gyldig navn',
  // Shift Data Pages
  taxi_owners_description: 'Administrer drosjeeiere og deres flåter',
  taxi_information_description: 'Administrer kjøretøydetaljer og taksameter-informasjon',
  income_shift_report_description: 'Se skiftdata og inntektsrapporter etter sjåfør og kjøretøy',
  add_owner: 'Legg til eier',
  add_vehicle: 'Legg til kjøretøy',
  total_owners: 'Totalt antall eiere',
  meter_number: 'Taksameternummer',
  last_inspection: 'Siste inspeksjon',
  maintenance: 'Vedlikehold',
  shift_date: 'Dato',
  shift_time: 'Skifttid',
  gross_income: 'Brutto',
  net_income: 'Netto',
  shifts: 'Skift',
  total_hours: 'Timer',
  gross: 'Brutto',
  net: 'Netto',
  all_status: 'Alle statuser',
  trips: 'Turer',
  // Language Menu
  language_updated: 'Språk endret til',
  select_language: 'Velg språk',

  // Partner pages / settings (missing in NB)
  quick_links: 'Hurtigkoblinger',
  follow_us: 'Følg oss',
  next: 'Neste',
  book_your_ride_menu: 'Bestill en tur',
  partners: 'Partnere',
  partner_pages: 'Partnersider',
  partner_slug: 'Partner-slug',
  partner_name: 'Partnernavn',
  fleetadmin_uid: 'Flåteadmin UID',
  subdomain: 'Underdomene',
  domains: 'Domener',
  partner_vanity: 'Egendefinert sti',
  partner_vanity_help: 'Valgfri tilpasset sti for delingslenker.',
  custom_url: 'Tilpasset URL',
  custom_url_help: 'Valgfri tilpasset URL for QR-koder og deling.',
  partner_maintenance_mode: 'Vedlikeholdsmodus',
  partner_maintenance_message: 'Vedlikeholdsmelding',
  access_control: 'Tilgangskontroll',
  access_control_subtitle: 'Definer roller, tillatelser og datatilgang for partnere og team.',
  role_permissions: 'Rolle-tillatelser',
  user_access: 'Brukertilgang',
  manage_role_permissions: 'Aktiver les, opprett, oppdater og slett.',
  permissions: 'Tillatelser',
  perm_read: 'Les',
  perm_create: 'Opprett',
  perm_update: 'Oppdater',
  perm_delete: 'Slett',
  reset_defaults: 'Tilbakestill til standard',
  save_changes: 'Lagre endringer',
  data_scope: 'Datatilgang',
  scope_all: 'Alle data',
  scope_partner: 'Partnerdata',
  scope_fleet: 'Flåtedata',
  scope_self: 'Kun egne data',
  allowed_partners: 'Tillatte partnere',
  allowed_fleets: 'Tillatte flåter',
  custom_permissions: 'Tilpassede tillatelser',
  custom: 'Tilpasset',
  edit_permissions: 'Rediger tillatelser',
  no_users: 'Ingen brukere funnet',
  no_access: 'Ingen tilgang',
  no_access_partners: 'Du har ikke tilgang til partner-sider.',
  website_studio: 'Nettstedsstudio',
  website_studio_subtitle: 'Oppdater landingssiden, merkevaren og navigasjonen.',
  content_language: 'Innholdsspråk',
  template_gallery: 'Mal-galleri',
  template_gallery_subtitle: 'Velg en layoutstil for landingssiden.',
  jump_to_section: 'Hopp til seksjon',
  preview_site: 'Forhåndsvis side',
  sections: 'Seksjoner',
  selected: 'Valgt',
  branding_seo: 'Merkevare og SEO',
  brand_name: 'Merkenavn',
  brand_logo: 'Logo-URL',
  favicon_url: 'Favicon-URL',
  seo_title: 'SEO-tittel',
  seo_description: 'SEO-beskrivelse',
  og_image: 'OpenGraph-bilde',
  navigation_footer: 'Navigasjon og bunntekst',
  nav_ride_links: 'Kjør-menylenker',
  nav_drive_links: 'Kjør som sjåfør-menylenker',
  nav_about_links: 'Om-menylenker',
  footer_company_links: 'Bunntekst – firma',
  footer_product_links: 'Bunntekst – produkter',
  footer_legal_links: 'Bunntekst – juridisk',
  homepage_sections: 'Hjemmeseksjoner',
  hero_section: 'Heltedelen',
  hero_title_label: 'Helte-tittel',
  hero_subtitle_label: 'Helte-undertittel',
  hero_image_label: 'Heltebilde-URL',
  primary_cta_label: 'Primær CTA',
  secondary_cta_label: 'Sekundær CTA',
  map_section: 'Kartseksjon',
  map_title_label: 'Karttittel',
  how_it_works_label: 'Slik fungerer det',
  how_it_works_title_label: 'Seksjonstittel',
  value_props_label: 'Verdiforslag',
  value_cards_label: 'Verdikort',
  safety_section_label: 'Sikkerhetsseksjon',
  safety_title_label: 'Sikkerhetstittel',
  safety_description_label: 'Sikkerhetsbeskrivelse',
  safety_link_text_label: 'Sikkerhetslenketekst',
  driver_business_section: 'Sjåfør og bedrift',
  driver_title_label: 'Sjåførtittel',
  driver_description_label: 'Sjåførbeskrivelse',
  driver_image_label: 'Sjåførbilde-URL',
  driver_primary_cta_label: 'Sjåfør primær CTA',
  driver_secondary_cta_label: 'Sjåfør sekundær CTA',
  driver_primary_link_label: 'Sjåfør primær lenke',
  driver_secondary_link_label: 'Sjåfør sekundær lenke',
  business_title_label: 'Bedriftstittel',
  business_description_label: 'Bedriftsbeskrivelse',
  business_link_label: 'Bedriftslenke',
  business_link_text_label: 'Bedriftslenketekst',
  business_features_label: 'Bedriftsfunksjoner',
  download_section: 'Nedlastingsseksjon',
  download_title_label: 'Nedlastingstittel',
  download_subtitle_label: 'Nedlastingsundertittel',
  show_apple_label: 'Vis Apple-knapp',
  show_android_label: 'Vis Android-knapp',
  notification_campaigns: 'Kampanjer',
  notification_templates: 'Maler',
  notification_segments: 'Segmenter',
  send_notification: 'Send varsling',
  apply_template: 'Bruk mal',
  apply_segment: 'Bruk segment',
  title_body_required: 'Tittel og melding er påkrevd',
  created_at: 'Opprettet',
  team_members: 'Teammedlemmer',
  load_defaults: 'Last standarder',
  label: 'Etikett',
  path: 'Sti',
  title: 'Tittel',
  link: 'Lenke',
  image: 'Bilde-URL',
  visible: 'Synlig',
  icon: 'Ikon',
  description: 'Beskrivelse',
  link_text: 'Lenketekst',
  hero_driver_card: 'Vis sjåførkort',
  hero_driver_title: 'Tittel for sjåførkort',
  hero_driver_subtitle: 'Undertittel for sjåførkort',
  roles: 'Roller',
  partner_maintenance_help: 'Vises til kunder når vedlikehold er aktivert.',
  saving: 'Lagrer',
  preview_as_customer: 'Forhåndsvis som kunde',
  partner_logo_url: 'Logo-URL',
  partner_favicon_url: 'Favicon-URL',
  partner_page_title: 'Sidetittel',
  partner_meta_description: 'Meta-beskrivelse',
  partner_og_image: 'Bilde for deling i sosiale medier',
  partner_support_email: 'Support e-post',
  partner_support_phone: 'Support telefon',
  partner_support_address: 'Support adresse',
  landing_templates: 'Landingsmaler',
  landing_templates_subtitle: 'Velg layout og palett tilpasset partneren.',
  domain_mode: 'Domene-modus',
  domain_mode_subdomain: 'Underdomene på hoveddomenet',
  domain_mode_standalone: 'Eget domene',
  domain_mode_both: 'Begge',
  domain_mode_help: 'Velg underdomene, eget domene eller begge.',
  domain_mode_standalone_help: 'Egne domener, kommaseparert.',
  logo_url: 'Logo-URL',
  hero_image: 'Toppbilde',
  cta_label: 'CTA-tekst',
  map_center_lat: 'Kartets senter (breddegrad)',
  map_center_lng: 'Kartets senter (lengdegrad)',
  theme_header: 'Temaoverskrift',
  theme_header_text: 'Tekst for temaoverskrift',
  theme_background: 'Temabakgrunn',
  partner_slug_required: 'Partner-slug er påkrevd',
  partner_slug_exists: 'Partner-slug finnes allerede',
  widget_preset: 'Widget-forvalg',
  widgets_csv: 'Widgets (CSV)',
  theme_json: 'Tema-JSON',
  theme_json_invalid: 'Tema-JSON er ugyldig',

  // Additional NB keys from master translations (to avoid EN fallbacks)
  pruduct_section_heading_1: 'Alltid tilgjengelig',
  product_section_1: 'Be om skyss, dag eller natt.',
  pruduct_section_heading_2: 'På tide',
  product_section_2: 'Pålitelige pickuper når du trenger dem.',
  pruduct_section_heading_3: 'Forsikret og kontrollert',
  product_section_3: 'Trygghet på hver tur.',
  pruduct_section_heading_4: 'Pålitelige sjåfører',
  product_section_4: 'Lokale eksperter bak rattet.',
  best_service_provider: 'Hvorfor sjåfører velger oss',
  service_start_soon: 'Lansering i byen din snart',
  drive_when_you_want: 'Kjør når du vil',
  drive_subtitle: 'Tjen penger på din egen timeplan. Registrer deg for å kjøre og tjene.',
  make_money_when_you_need: 'Tjen penger når du trenger det',
  money_subtitle: 'Fleksibel inntjening med forhåndspriser og støtte i appen.',
  grow_taxi_business: 'Utvid flåten din',
  taxi_subtitle: 'Administrer kjøretøyene dine, følg ytelsen, og skaler virksomheten din.',
  get_started: 'Kom i gang',
  learn_more: 'Lær mer',
  become_partner: 'Bli partner',
  flexible_hours: 'Fleksible timer',
  flexible_hours_desc: 'Kjør når du vil. Sett din egen timeplan og jobb så mye eller lite du ønsker.',
  upfront_fares: 'Priser på forhånd',
  upfront_fares_desc: 'Se turpriser før du aksepterer. Ingen overraskelser, bare klare priser.',
  add_vehicles: 'Legg til kjøretøy og sjåfører',
  fleet_dashboard: 'Flåteoversikt',
  quick_actions: 'Raske handlinger',
  my_cars: 'Mine biler',
  dashboard: 'Dashbord',
  book_now: 'Bestill nå',
  min_fare: 'Minimumspris',
  extra_info: 'Ekstra info',
  mile: 'mil',
  km: 'kilometer',
  myaccount: 'Min konto',
  login_signup: 'Logg inn / Registrer deg',
  about_us: 'Om oss',
  contact_us: 'Kontakt oss',
  privacy_policy: 'Personvernerklæring',
  download_apps_title: 'Last ned appene og kom i gang',
  download_apps_subtitle: 'Tilgjengelig for iOS og Android. Få appen som passer dine behov.',
  quick_book: 'Rask bok',
  start_booking: 'Begynn å bestille',
  currency_settings: 'Valutainnstillinger',
  currency_symbol: 'Valutasymbol',
  currency_code: 'Valutakode',
  set_decimal: 'Sett desimal',
  swipe_symbol_direction: 'Sveip symbolretning',
  disable_online_payments: 'Deaktiver nettbetalinger',
  disable_cash_payments: 'Deaktiver kontantbetalinger',
  maintenance_settings: 'Vedlikeholdsinnstillinger',
  maintenance_mode: 'Vedlikeholdsmodus',
  maintenance_message: 'Vedlikeholdsmelding',
  security_settings: 'Sikkerhetsinnstillinger',
  booking_otp: 'Bestillingskode (OTP)',
  driver_approval: 'Sjåførgodkjenning',
  car_approval: 'Bilgodkjenning',
  verify_id_password: 'Bekreft ID / passord',
  referral_bonus: 'Henvisningsbonus',
  wallet_denominations: 'Lommebok-valører',
  advance_settings: 'Forhåndsinnstillinger',
  select_country: 'Velg land',
  autocomplete_country_restriction: 'Landsbegrensning for autofullføring',
  allow_multi_country_selection: 'Tillat valg av flere land',
  convert_to_mile: 'Konverter til mil',
  repeat_sound_on_new_trip: 'Gjenta lyd på ny tur',
  allow_delivery_pick_image: 'Tillat levering plukke bilde',
  allow_delivery_drop_image: 'Tillat leveringsbilde',
  customer_withdraw: 'Kunden trekker seg',
  car_list_view_horizontal: 'Billistevisning horisontalt',
  use_distance_matrix_api: 'Bruk Distance Matrix API',
  driver_settings: 'Driverinnstillinger',
  driver_radius: 'Sjåfør-radius',
  auto_dispatch: 'Automatisk utsendelse',
  allow_driver_negative_balance: 'Tillat sjåfør negativ saldo',
  bank_reg_fields: 'Bankregistreringsfelt',
  show_live_route: 'Vis direkte rute',
};

// Spanish – best-effort human translations for primary UI strings, rest fallback to English
const defaultEs = {
  ...defaultEn,
  home: 'Inicio',
  my_bookings: 'Mis reservas',
  wallet: 'Billetera',
  book_a_ride: 'Reserva un viaje',
  drive_with_us: 'Conduce con nosotros',
  why_choose_plukk: 'Por qué elegir Plukk Taxi',
  vp_subtitle: 'Recogidas rápidas, precios justos y buen soporte.',
  vp_safety: 'Seguridad primero',
  vp_safety_desc: 'Conductores verificados y soporte en la app.',
  vp_upfront: 'Precio transparente',
  vp_upfront_desc: 'Ve la tarifa por adelantado — sin sorpresas.',
  vp_schedule: 'Recogidas programadas',
  vp_schedule_desc: 'Reserva con antelación para viajar tranquilo.',
  vp_support: 'Soporte 24/7',
  vp_support_desc: 'Estamos aquí si necesitas ayuda.',
  language: 'Idioma',
  security: 'Seguridad',
  privacy_data: 'Privacidad y datos',
  apply_language: 'Actualizar idioma',
  get_there: 'Llega rápido',
  avg_pickup_time: 'Tiempo medio de recogida',
  available: 'Disponible',
  active_drivers: 'Conductores activos',
  simple_process: 'Proceso sencillo',
  why_us: 'Por qué elegirnos',
  available_now: 'Disponible ahora',
  free_download: 'Descarga gratis',
  download_app: 'Descargar app',
  weekly_payouts: 'Pagos semanales',
  detailed_analytics: 'Analítica detallada',
  ride: 'Viaje',
  reserve: 'Reservar',
  teens: 'Adolescentes',
  seniors: 'Mayores',
  book_now: 'Reserva ahora',
  book_later: 'Programar',
  pickup_location: 'Lugar de recogida',
  drop_location: 'Destino',
  choose_ride: 'Elige vehículo',
  campaigns: 'Campañas',
  recommendations: 'Recomendaciones',
  edit_booking: 'Editar reserva',
  view_trip: 'Ver viaje',
  driver_assigned: 'Un conductor ha sido asignado a tu viaje',
  driver_assigned_success: 'Conductor asignado con éxito',
  active_trip: 'Viaje activo',
  active_trip_hint: 'Tienes un viaje en curso. Toca para ver detalles.',
  view_details: 'Ver detalles',
  no_bookings_yet: 'Sin reservas aún',
  no_bookings_help: 'Empieza creando tu primera reserva.',
  car_types: 'Tipos de vehículo',
  security_info: 'Puedes cambiar tu correo o teléfono en Información personal. Por seguridad, verificamos cambios con un código único (OTP).',
  tip_strong_password: 'Consejo: usa una contraseña segura y no compartas códigos de un solo uso con nadie.',
  privacy_blurb: 'Solo recopilamos lo necesario para tu cuenta y viajes. Puedes actualizar tus datos cuando quieras o pedir al soporte revisar, exportar o borrar tu información.',
  request_ride: 'Solicitar viaje',
  pickup_now: 'Recoger ahora',
  schedule: 'Programar para después',
  suggestions: 'Sugerencias',
  select_user: 'Seleccionar usuario',
  enter_pickup: 'Introduce punto de recogida',
  enter_destination: 'Introduce destino',
  see_prices: 'Ver precios',
  add_phone: 'Agregar teléfono',
  add_email: 'Agregar correo',
  confirm_and_request: 'Confirmar y solicitar',
  payment_mode: 'Método de pago',
  wallet_balance_low: 'Saldo de billetera insuficiente',
  wallet_booking_alert: '¿Usar la billetera para esta reserva?',
  use_wallet_balance: 'Usar billetera',
  paynow_button: 'Pagar ahora',
  add_pickup_note: 'Agregar nota de recogida',
  ride_options: 'Opciones de viaje',
  parcel_types: 'Tipos de paquete',
  options: 'Opciones',
  confirm_ride: 'Confirmar viaje',
  confirm_pickup_location: 'Confirmar punto de recogida',
  distance: 'Distancia',
  time: 'Tiempo',
  fare: 'Tarifa',
  faster: 'Más rápido',
  personal: 'Personal',
  enter_destination_hint: 'Añade destino para ver el precio',
  request: 'Solicitar',
  select_proper: 'Completa todos los campos requeridos',
  place_to_coords_error: 'No se pudo ubicar esa dirección'
};

// French – best-effort human translations for primary UI strings, rest fallback to English
const defaultFr = {
  ...defaultEn,
  home: 'Accueil',
  my_bookings: 'Mes réservations',
  wallet: 'Portefeuille',
  book_a_ride: 'Réserver un trajet',
  drive_with_us: 'Conduisez avec nous',
  why_choose_plukk: 'Pourquoi choisir Plukk Taxi',
  vp_subtitle: 'Prises en charge rapides, prix justes et bon support.',
  vp_safety: 'Sécurité d’abord',
  vp_safety_desc: 'Chauffeurs vérifiés et assistance dans l’app.',
  vp_upfront: 'Tarif transparent',
  vp_upfront_desc: 'Voyez le prix à l’avance — pas de surprises.',
  vp_schedule: 'Prises en charge programmées',
  vp_schedule_desc: 'Réservez à l’avance pour plus de sérénité.',
  vp_support: 'Support 24/7',
  vp_support_desc: 'Nous sommes là si vous avez besoin d’aide.',
  language: 'Langue',
  security: 'Sécurité',
  privacy_data: 'Confidentialité et données',
  apply_language: 'Mettre à jour la langue',
  get_there: 'Arrivez vite',
  avg_pickup_time: 'Temps moyen de prise en charge',
  available: 'Disponible',
  active_drivers: 'Chauffeurs actifs',
  simple_process: 'Processus simple',
  why_us: 'Pourquoi nous choisir',
  available_now: 'Disponible maintenant',
  free_download: 'Téléchargement gratuit',
  download_app: 'Télécharger l’app',
  weekly_payouts: 'Paiements hebdomadaires',
  detailed_analytics: 'Analyses détaillées',
  ride: 'Course',
  reserve: 'Réserver',
  teens: 'Ados',
  seniors: 'Seniors',
  book_now: 'Réserver maintenant',
  book_later: 'Programmer',
  pickup_location: 'Lieu de prise en charge',
  drop_location: 'Destination',
  choose_ride: 'Choisir un véhicule',
  campaigns: 'Campagnes',
  recommendations: 'Recommandations',
  edit_booking: 'Modifier la réservation',
  view_trip: 'Voir le trajet',
  driver_assigned: 'Un chauffeur a été assigné à votre trajet',
  driver_assigned_success: 'Chauffeur assigné avec succès',
  active_trip: 'Trajet actif',
  active_trip_hint: 'Vous avez un trajet en cours. Appuyez pour voir les détails.',
  view_details: 'Voir les détails',
  no_bookings_yet: 'Aucune réservation pour le moment',
  no_bookings_help: 'Commencez par créer votre première réservation.',
  car_types: 'Types de véhicule',
  security_info: 'Vous pouvez modifier votre email ou téléphone dans Informations personnelles. Par sécurité, nous vérifions via un code unique (OTP).',
  tip_strong_password: 'Astuce : utilisez un mot de passe robuste et ne partagez jamais les codes à usage unique.',
  privacy_blurb: 'Nous collectons uniquement ce qui est nécessaire pour votre compte et vos trajets. Vous pouvez mettre à jour vos données ou demander au support de les consulter, exporter ou supprimer.',
  request_ride: 'Demander une course',
  pickup_now: 'Prendre maintenant',
  schedule: 'Planifier plus tard',
  suggestions: 'Suggestions',
  select_user: 'Choisir un utilisateur',
  enter_pickup: 'Entrer le lieu de départ',
  enter_destination: 'Entrer la destination',
  see_prices: 'Voir les prix',
  add_phone: 'Ajouter un téléphone',
  add_email: 'Ajouter un e-mail',
  confirm_and_request: 'Confirmer et demander',
  payment_mode: 'Mode de paiement',
  wallet_balance_low: 'Solde du portefeuille insuffisant',
  wallet_booking_alert: 'Utiliser le portefeuille pour cette réservation ?',
  use_wallet_balance: 'Utiliser le portefeuille',
  paynow_button: 'Payer maintenant',
  add_pickup_note: 'Ajouter une note de prise en charge',
  ride_options: 'Options de trajet',
  parcel_types: 'Types de colis',
  options: 'Options',
  confirm_ride: 'Confirmer la course',
  confirm_pickup_location: 'Confirmer le lieu de prise en charge',
  distance: 'Distance',
  time: 'Temps',
  fare: 'Tarif',
  faster: 'Plus rapide',
  personal: 'Personnel',
  enter_destination_hint: 'Ajoutez une destination pour voir le prix',
  request: 'Demander',
  select_proper: 'Veuillez remplir tous les champs requis',
  place_to_coords_error: 'Adresse introuvable'
};

// Arabic – best-effort translations for primary UI strings
const defaultAr = {
  ...defaultEn,
  home: 'الرئيسية',
  my_bookings: 'حجوزاتي',
  wallet: 'المحفظة',
  book_a_ride: 'احجز رحلة',
  drive_with_us: 'قد معنا',
  why_choose_plukk: 'لماذا تختار Plukk Taxi',
  language: 'اللغة',
  security: 'الأمان',
  privacy_data: 'الخصوصية والبيانات',
  ride: 'رحلة',
  reserve: 'حجز',
  teens: 'مراهقون',
  seniors: 'كبار السن',
  apply_language: 'تحديث اللغة',
  get_there: 'الوصول بسرعة',
  avg_pickup_time: 'متوسط زمن الوصول',
  available: 'متاح',
  active_drivers: 'سائقون نشطون',
  simple_process: 'عملية بسيطة',
  why_us: 'لماذا نحن',
  available_now: 'متاح الآن',
  free_download: 'تحميل مجاني',
  download_app: 'حمّل التطبيق',
  weekly_payouts: 'مدفوعات أسبوعية',
  detailed_analytics: 'تحليلات مفصلة',
  book_now: 'احجز الآن',
  book_later: 'جدولة',
  pickup_location: 'موقع الانطلاق',
  drop_location: 'موقع الوصول',
  choose_ride: 'اختر مركبة',
  campaigns: 'حملات',
  recommendations: 'توصيات',
  edit_booking: 'تعديل الحجز',
  view_trip: 'عرض الرحلة',
  driver_assigned: 'تم تعيين سائق لرحلتك',
  driver_assigned_success: 'تم تعيين السائق بنجاح',
  active_trip: 'رحلة نشطة',
  active_trip_hint: 'لديك رحلة جارية. اضغط لعرض التفاصيل.',
  view_details: 'عرض التفاصيل',
  no_bookings_yet: 'لا توجد حجوزات بعد',
  no_bookings_help: 'ابدأ بإنشاء أول حجز لك.',
  car_types: 'أنواع المركبات',
  security_info: 'يمكنك تغيير البريد أو الهاتف من المعلومات الشخصية. للحماية، نتحقق بالتكويد لمرة واحدة (OTP).',
  tip_strong_password: 'نصيحة: استخدم كلمة مرور قوية ولا تشارك رموز OTP مع أي شخص.',
  privacy_blurb: 'نجمع فقط المعلومات الضرورية لحسابك ورحلاتك. يمكنك تحديث بياناتك أو طلب مراجعتها أو تصديرها أو حذفها عبر الدعم.',
  request_ride: 'طلب رحلة',
  pickup_now: 'استلام الآن',
  schedule: 'جدولة لاحقاً',
  suggestions: 'اقتراحات',
  select_user: 'اختر مستخدماً',
  enter_pickup: 'أدخل موقع الانطلاق',
  enter_destination: 'أدخل الوجهة',
  see_prices: 'عرض الأسعار',
  add_phone: 'أضف رقم الهاتف',
  add_email: 'أضف البريد الإلكتروني',
  confirm_and_request: 'تأكيد وطلب',
  payment_mode: 'طريقة الدفع',
  wallet_balance_low: 'رصيد المحفظة منخفض',
  wallet_booking_alert: 'استخدام رصيد المحفظة لهذا الحجز؟',
  use_wallet_balance: 'استخدام المحفظة',
  paynow_button: 'ادفع الآن',
  add_pickup_note: 'أضف ملاحظة للالتقاط',
  ride_options: 'خيارات الرحلة',
  parcel_types: 'أنواع الطرود',
  options: 'خيارات',
  confirm_ride: 'تأكيد الرحلة',
  confirm_pickup_location: 'تأكيد موقع الالتقاط',
  distance: 'المسافة',
  time: 'الوقت',
  fare: 'الأجرة',
  faster: 'أسرع',
  personal: 'شخصي',
  enter_destination_hint: 'أضف الوجهة لعرض السعر',
  request: 'طلب',
  select_proper: 'يرجى إكمال جميع الحقول المطلوبة',
  place_to_coords_error: 'تعذر تحديد العنوان'
};

// Italian – best-effort translations
const defaultIt = {
  ...defaultEn,
  home: 'Home',
  my_bookings: 'Le mie prenotazioni',
  wallet: 'Portafoglio',
  book_a_ride: 'Prenota una corsa',
  drive_with_us: 'Guida con noi',
  why_choose_plukk: 'Perché scegliere Plukk Taxi',
  language: 'Lingua',
  security: 'Sicurezza',
  privacy_data: 'Privacy e dati',
  apply_language: 'Aggiorna lingua',
  get_there: 'Arriva subito',
  avg_pickup_time: 'Tempo medio di ritiro',
  available: 'Disponibile',
  active_drivers: 'Autisti attivi',
  simple_process: 'Processo semplice',
  why_us: 'Perché noi',
  available_now: 'Disponibile ora',
  free_download: 'Download gratuito',
  download_app: "Scarica l'app",
  weekly_payouts: 'Pagamenti settimanali',
  detailed_analytics: 'Analisi dettagliate',
  ride: 'Corsa',
  reserve: 'Prenota',
  teens: 'Teen',
  seniors: 'Senior',
  book_now: 'Prenota ora',
  book_later: 'Programma',
  pickup_location: 'Punto di partenza',
  drop_location: 'Destinazione',
  choose_ride: 'Scegli veicolo',
  campaigns: 'Campagne',
  recommendations: 'Consigli',
  edit_booking: 'Modifica prenotazione',
  view_trip: 'Vedi corsa',
  driver_assigned: 'Un autista è stato assegnato',
  driver_assigned_success: 'Autista assegnato con successo',
  active_trip: 'Corsa attiva',
  active_trip_hint: 'Hai una corsa in corso. Tocca per i dettagli.',
  view_details: 'Vedi dettagli',
  no_bookings_yet: 'Nessuna prenotazione ancora',
  no_bookings_help: 'Inizia creando la tua prima prenotazione.',
  car_types: 'Tipi di veicolo',
  security_info: 'Puoi cambiare email o telefono in Info personali. Per sicurezza verifichiamo con un codice OTP.',
  tip_strong_password: 'Suggerimento: usa una password forte e non condividere i codici OTP con nessuno.',
  privacy_blurb: 'Raccogliamo solo ciò che serve per l’account e le corse. Puoi aggiornare i dati in ogni momento o chiedere al supporto di consultarli, esportarli o eliminarli.',
  request_ride: 'Richiedi una corsa',
  pickup_now: 'Parti ora',
  schedule: 'Programma per dopo',
  suggestions: 'Suggerimenti',
  select_user: 'Seleziona utente',
  enter_pickup: 'Inserisci punto di partenza',
  enter_destination: 'Inserisci destinazione',
  see_prices: 'Vedi prezzi',
  add_phone: 'Aggiungi telefono',
  add_email: 'Aggiungi email',
  confirm_and_request: 'Conferma e richiedi',
  payment_mode: 'Metodo di pagamento',
  wallet_balance_low: 'Saldo portafoglio insufficiente',
  wallet_booking_alert: 'Usare il portafoglio per questa prenotazione?',
  use_wallet_balance: 'Usa portafoglio',
  paynow_button: 'Paga ora',
  add_pickup_note: 'Aggiungi nota di ritiro',
  ride_options: 'Opzioni corsa',
  parcel_types: 'Tipi di pacco',
  options: 'Opzioni',
  confirm_ride: 'Conferma corsa',
  confirm_pickup_location: 'Conferma punto di ritiro',
  distance: 'Distanza',
  time: 'Tempo',
  fare: 'Tariffa',
  faster: 'Più veloce',
  personal: 'Personale',
  enter_destination_hint: 'Aggiungi destinazione per vedere il prezzo',
  request: 'Richiedi',
  select_proper: 'Compila tutti i campi obbligatori',
  place_to_coords_error: 'Impossibile trovare l’indirizzo'
};

// Chinese (Simplified) – best-effort translations
const defaultZh = {
  ...defaultEn,
  home: '首页',
  my_bookings: '我的订单',
  wallet: '钱包',
  book_a_ride: '预订行程',
  drive_with_us: '加入司机团队',
  why_choose_plukk: '为什么选择 Plukk Taxi',
  language: '语言',
  security: '安全',
  privacy_data: '隐私与数据',
  apply_language: '更新语言',
  get_there: '快速到达',
  avg_pickup_time: '平均接驾时间',
  available: '可用',
  active_drivers: '活跃司机',
  simple_process: '简单流程',
  why_us: '为什么选择我们',
  available_now: '立即可用',
  free_download: '免费下载',
  download_app: '下载应用',
  weekly_payouts: '每周结算',
  detailed_analytics: '详细分析',
  ride: '行程',
  reserve: '预约',
  teens: '青少年',
  seniors: '老人',
  book_now: '立即预订',
  book_later: '稍后安排',
  pickup_location: '上车地点',
  drop_location: '下车地点',
  choose_ride: '选择车型',
  campaigns: '活动',
  recommendations: '推荐',
  edit_booking: '编辑订单',
  view_trip: '查看行程',
  driver_assigned: '已为你指派司机',
  driver_assigned_success: '司机指派成功',
  active_trip: '进行中的行程',
  active_trip_hint: '你有正在进行的行程，点击查看详情。',
  view_details: '查看详情',
  no_bookings_yet: '暂无订单',
  no_bookings_help: '创建你的第一笔订单吧。',
  car_types: '车型',
  security_info: '您可以在个人信息中修改邮箱或电话，为安全起见我们会用一次性验证码验证。',
  tip_strong_password: '提示：请使用强密码，不要与他人分享一次性验证码。',
  privacy_blurb: '我们只收集账户和行程所需的最少信息。你可以随时更新资料，或联系支持查看、导出或删除你的数据。',
  request_ride: '叫车',
  pickup_now: '立即出发',
  schedule: '稍后预约',
  suggestions: '推荐',
  select_user: '选择用户',
  enter_pickup: '输入上车地点',
  enter_destination: '输入下车地点',
  see_prices: '查看价格',
  add_phone: '添加手机号',
  add_email: '添加邮箱',
  confirm_and_request: '确认并请求',
  payment_mode: '支付方式',
  wallet_balance_low: '钱包余额不足',
  wallet_booking_alert: '使用钱包余额支付本次订单？',
  use_wallet_balance: '使用钱包',
  paynow_button: '立即支付',
  add_pickup_note: '添加上车备注',
  ride_options: '出行选项',
  parcel_types: '包裹类型',
  options: '选项',
  confirm_ride: '确认行程',
  confirm_pickup_location: '确认上车地点',
  distance: '距离',
  time: '时间',
  fare: '费用',
  faster: '更快',
  personal: '个人',
  enter_destination_hint: '添加目的地以查看价格',
  request: '请求',
  select_proper: '请完成所有必填字段',
  place_to_coords_error: '无法定位该地址'
};

// Japanese – best-effort translations
const defaultJa = {
  ...defaultEn,
  home: 'ホーム',
  my_bookings: '予約一覧',
  wallet: 'ウォレット',
  book_a_ride: '乗車を予約',
  drive_with_us: '一緒にドライブ',
  why_choose_plukk: 'Plukk Taxiを選ぶ理由',
  language: '言語',
  security: 'セキュリティ',
  privacy_data: 'プライバシーとデータ',
  apply_language: '言語を更新',
  get_there: 'すぐに移動',
  avg_pickup_time: '平均配車時間',
  available: '利用可能',
  active_drivers: '稼働中のドライバー',
  simple_process: 'シンプルな流れ',
  why_us: '選ばれる理由',
  available_now: '今すぐ利用可',
  free_download: '無料ダウンロード',
  download_app: 'アプリをダウンロード',
  weekly_payouts: '週次支払い',
  detailed_analytics: '詳細分析',
  ride: '乗車',
  reserve: '予約',
  teens: 'ティーン',
  seniors: 'シニア',
  book_now: '今すぐ予約',
  book_later: 'あとで予約',
  pickup_location: '乗車場所',
  drop_location: '降車場所',
  choose_ride: '車種を選ぶ',
  campaigns: 'キャンペーン',
  recommendations: 'おすすめ',
  edit_booking: '予約を編集',
  view_trip: '行程を見る',
  driver_assigned: 'ドライバーが割り当てられました',
  driver_assigned_success: 'ドライバーの割り当てに成功しました',
  active_trip: '進行中の乗車',
  active_trip_hint: '進行中の乗車があります。詳細を見るにはタップ。',
  view_details: '詳細を見る',
  no_bookings_yet: '予約はまだありません',
  no_bookings_help: '最初の予約を作成しましょう。',
  car_types: '車種',
  security_info: 'メールや電話は「個人情報」から変更できます。安全のためOTPで確認します。',
  tip_strong_password: 'ヒント：強力なパスワードを使い、ワンタイムコードは共有しないでください。',
  privacy_blurb: 'アカウントと乗車に必要な最小限の情報だけを収集します。いつでも情報を更新したり、サポートに連絡して確認・エクスポート・削除を依頼できます。',
  request_ride: '乗車をリクエスト',
  pickup_now: '今すぐ配車',
  schedule: '後で予約',
  suggestions: 'おすすめ',
  select_user: 'ユーザーを選択',
  enter_pickup: '乗車場所を入力',
  enter_destination: '降車場所を入力',
  see_prices: '料金を見る',
  add_phone: '電話番号を追加',
  add_email: 'メールを追加',
  confirm_and_request: '確認してリクエスト',
  payment_mode: '支払い方法',
  wallet_balance_low: 'ウォレット残高が不足しています',
  wallet_booking_alert: 'この予約でウォレットを使いますか？',
  use_wallet_balance: 'ウォレットを使う',
  paynow_button: '今すぐ支払う',
  add_pickup_note: '乗車メモを追加',
  ride_options: '乗車オプション',
  parcel_types: '荷物の種類',
  options: 'オプション',
  confirm_ride: '乗車を確認',
  confirm_pickup_location: '乗車場所を確認',
  distance: '距離',
  time: '時間',
  fare: '料金',
  faster: 'より速く',
  personal: '個人',
  enter_destination_hint: '目的地を追加して料金を確認',
  request: 'リクエスト',
  select_proper: '必須項目をすべて入力してください',
  place_to_coords_error: '住所を特定できませんでした'
};

// Amharic – best-effort translations for primary UI strings
const defaultAm = {
  ...defaultEn,
  // Navigation
  ride: 'ጉዞ',
  drive: 'ነዳ',
  about: 'ስለ',
  company: 'ስለ ኩባንያ',
  products: 'ምርቶች',
  business: 'ለንግድ',
  privacy: 'ግላዊነት',
  terms: 'ውሎች',
  language: 'ቋንቋ',
  // Auth
  signIn: 'ግባ',
  login: 'ግባ',
  signup: 'ተመዝገብ',
  enter_email_or_mobile: 'ስልክ ቁጥር ወይም ኢሜል ያስገቡ',
  contact_placeholder: 'ስልክ ቁጥር ወይም ኢሜል',
  we_will_send_otp: 'የማረጋገጫ ኮድ እንልካለን',
  continue: 'ቀጥል',
  resend_otp: 'ኮድ እንደገና ላክ',
  resend_in: 'እንደገና ላክ በ',
  enter_code: 'ኮድ ያስገቡ',
  code_sent_to: 'ኮድ ልከናል ወደ',
  verify: 'አረጋግጥ',
  back: 'ተመለስ',
  signin_with_vipps: 'በVipps ቀጥል',
  signin_with_google: 'በGoogle ቀጥል',
  signin_with_facebook: 'በFacebook ቀጥል',
  use_password_login: 'በይለፍ ቃል ይግቡ',
  password_login_description: 'በኢሜል እና የይለፍ ቃል ይግቡ',
  email_placeholder: 'የኢሜል አድራሻ',
  password: 'የይለፍ ቃል',
  create_account: 'መለያ ፍጠር',
  forgot_password: 'የይለፍ ቃል ረሱ?',
  use_otp_login: 'በማረጋገጫ ኮድ ይግቡ',
  sign_up: 'ተመዝገብ',
  signup_description: 'ለመጀመር ይመዝገቡ',
  first_name: 'ስም',
  last_name: 'የአባት ስም',
  or: 'ወይም',
  // Dialogs
  alert: 'ማስጠንቀቂያ',
  ok: 'እሺ',
  cancel: 'ሰርዝ',
  not_logged_in: 'አልገቡም',
  // Buttons
  learn_more: 'ተጨማሪ ይወቁ',
  get_started: 'ይጀምሩ',
  // Common
  home: 'ቤት',
  my_bookings: 'ቦኪንጎቼ',
  wallet: 'ዋሌት',
  book_a_ride: 'ጉዞ ይዘዙ',
  drive_with_us: 'ከእኛ ጋር ይንዱ',
  why_choose_plukk: 'ለምን ፕሉክ ይምረጡ',
  security: 'ደህንነት',
  privacy_data: 'ግላዊነት እና ዳታ',
  apply_language: 'ቋንቋ ያዘምኑ',
  get_there: 'እዚያ ድረስ',
  avg_pickup_time: 'አማካይ የማንሳት ጊዜ',
  available: 'ይገኛል',
  active_drivers: 'ንቁ ሹፌሮች',
  simple_process: 'ቀላል ሂደት',
  why_us: 'ለምን እኛን ይምረጡ',
  available_now: 'አሁን ይገኛል',
  free_download: 'ነጻ',
  download_app: 'መተግበሪያ ያውርዱ',
  weekly_payouts: 'ሳምንታዊ ክፍያዎች',
  detailed_analytics: 'ዝርዝር ትንተና',
  // Footer translations
  quick_links: 'ፈጣን አገናኞች',
  follow_us: 'ይከተሉን',
  myaccount: 'መለያዬ',
  // Landing page translations
  book_your_title: 'በፕሉክ የትም ሂድ',
  hero_title: 'በፕሉክ የትም ሂድ',
  about_us_content2: 'ጉዞ ይጠይቁ፣ ይግቡ እና ይሂዱ።',
  book_your_ride_menu: 'ጉዞ ይጠይቁ',
  how_it_works: 'እንዴት እንደሚሰራ',
  how_plukk_works: 'ፕሉክ እንዴት ይሰራል',
  hiw_subtitle: 'በጥቂት ንክኪዎች ጉዞ ያግኙ',
  hiw_step1_title: 'መድረሻዎን ያስገቡ',
  hiw_step1_desc: 'መተግበሪያውን ይክፈቱ፣ የት መሄድ እንደሚፈልጉ ያስገቡ፣ ያሉትን አማራጮች እናሳይዎታለን።',
  hiw_step2_title: 'ጉዞዎን ይምረጡ',
  hiw_step2_desc: 'የጉዞ አማራጮችን፣ ዋጋዎችን እና የደረሰበት ጊዜዎችን ያወዳድሩ። ለእርስዎ የሚሆነውን ይምረጡ።',
  hiw_step3_title: 'ይክፈሉ እና ይሂዱ',
  hiw_step3_desc: 'ጉዞዎን ያረጋግጡ፣ ሹፌርዎን በእውነተኛ ጊዜ ይከታተሉ እና በቀላሉ ይክፈሉ።',
  ride_with_plukk: 'ጉዞ ከፕሉክ ጋር',
  ride_with_plukk_desc: 'ጉዞ ይጠይቁ እና በቅርብ ያለ ሹፌር በጥቂት ደቂቃዎች ይደርስብዎታል።',
  drive_with_plukk: 'ከፕሉክ ጋር ይንዱ',
  drive_with_plukk_desc: 'የራስዎን ሰዓት ያቀናብሩ እና በሚፈልጉት ጊዜ ገንዘብ ያግኙ።',
  get_a_ride: 'ጉዞ ይዘዙ',
  become_a_driver: 'ሹፌር ይሁኑ',
  safety_first: 'ደህንነት ቀዳሚ ነው',
  safety_desc: 'በጀርባ ወይም በመሪ ቦታ ቢሆኑም ደህንነትዎ አስፈላጊ ነው። ጉዞዎችን ያስተማማን ዘንድ ሰዎችን ከቴክኖሎጂ ጋር እንጣመራለን።',
  learn_about_safety: 'ስለ ደህንነት ይማሩ',
  driver_screening: 'የሹፌር ምርመራ',
  driver_screening_desc: 'ከመንዳት በፊት የዳታ እና የታሪክ ምርመራ',
  real_time_support: 'ቀጥታ ድጋፍ',
  real_time_support_desc: '24/7 የደንበኛ አገልግሎት',
  trip_tracking: 'ጉዞ ክትትል',
  trip_tracking_desc: 'የጉዞ ሁኔታዎን ከቤተሰብ ጋር ይጋሩ',
  friendly_driver: 'ትሑት ሹፌር',
  drivers_near_you: 'በአቅራቢያዎ ያሉ ሹፌሮች',
  vp_safety: 'ደህንነት መጀመሪያ',
  vp_safety_desc: 'እያንዳንዱ ጉዞ የተረጋገጡ ሹፌሮች እና በመተግበሪያ ውስጥ ድጋፍ ይሰጣል።',
  vp_upfront: 'ግልጽ ዋጋ',
  vp_upfront_desc: 'ዋጋውን አስቀድመው ይመልከቱ — ምንም አስደንጋጭ ነገር የለም።',
  vp_schedule: 'የታቀዱ ማንሳቶች',
  vp_schedule_desc: 'ለልብ ሰላም ጉዞዎችን አስቀድመው ይያዙ።',
  vp_support: '24/7 እዚህ ነን',
  vp_support_desc: 'እርዳታ ከፈለጉ ወዳጃዊ ድጋፍ።',
  vp_business: 'የንግድ መለያዎች',
  vp_business_desc: 'ለቡድኖች ወርሃዊ ክፍያ እና የጉዞ ሪፖርቶች።',
  vp_green: 'አረንጓዴ አማራጮች',
  vp_green_desc: 'ሲገኝ ዝቅተኛ ልቀት ተሽከርካሪዎችን ይምረጡ።',
  vp_subtitle: 'ፈጣን ማንሳቶች፣ ፍትሃዊ ዋጋዎች፣ አጋዥ ድጋፍ።',
  driver: 'ሹፌር',
  drive_when_you_want: 'በፈለጉት ጊዜ ይንዱ',
  drive_subtitle: 'በራስዎ መርሃግብር ገንዘብ ያግኙ። ለመንዳት ይመዝገቡ።',
  earn_on_your_terms: 'በፈለጉት መርሃግብር ገንዘብ ያግኙ',
  grow_taxi_business: 'መርከብዎን ያሳድጉ',
  taxi_subtitle: 'ተሽከርካሪዎችዎን ያስተዳድሩ፣ አፈጻጸምን ይከታተሉ እና ንግድዎን ያሳድጉ።',
  become_partner: 'አጋር ይሁኑ',
  best_service_provider: 'ሹፌሮች ለምን እኛን ይመርጣሉ',
  flexible_hours: 'ተለዋዋጭ ሰዓታት',
  flexible_hours_desc: 'በፈለጉት ጊዜ ይንዱ። የራስዎን መርሃግብር ያዘጋጁ።',
  upfront_fares: 'ቅድመ ክፍያዎች',
  upfront_fares_desc: 'ከመቀበልዎ በፊት የጉዞ ዋጋዎችን ይመልከቱ። ግልጽ ዋጋ ብቻ።',
  add_vehicles: 'ተሽከርካሪዎች እና ሹፌሮች ያክሉ',
  fleet_dashboard: 'የመርከብ ዳሽቦርድ',
  driver_signup_subtitle: 'በፈለጉት ጊዜ ይንዱ፣ የሚፈልጉትን ያግኙ። የራስዎን ሰዓት ያቀናብሩ እና የምታገኙትን ይጠብቁ።',
  plukk_for_business: 'ፕሉክ ለንግድ',
  business_desc: 'የኩባንያዎን መንቀሳቀስ ያሻሽላል። ለሁሉም መጠን ቀላል የጉዞ አስተዳደር።',
  simplified_billing: 'ቀላል ክፍያ',
  simplified_billing_desc: 'ለሁሉም ጉዞ አንድ ወርሃዊ ደረሰኝ',
  central_dashboard: 'ዋና ዳሽቦርድ',
  central_dashboard_desc: 'ጉዞዎችን ይከታተሉ እና ተጠቃሚዎችን ያስተዳድሩ',
  expense_integration: 'የወጪ ማዋቀሪያ',
  expense_integration_desc: 'ከወጪ መሣሪያዎችዎ ጋር ይሰራል',
  mobile_apps_on_store: 'መተግበሪያውን ያግኙ',
  app_store_deception1: 'ነጻ መተግበሪያውን ያውርዱ እና ጉዞ ይጠይቁ ወይም ያሽከርክሩ እና በራስዎ መርሃግብር ገንዘብ ያግኙ።',
  download_brand_title: 'የምትፈልጉትን ጉዞ ያግኙ',
  download_brand_body: 'ፕሉክ ሁልጊዜ ዝግጁ ነው። ወደ ተለመዱት መድረሻዎች ቢሆን ወይም አዲስ ቦታ ቢሆን፣ ፕሉክ መተግበሪያውን ይክፈቱ እና በአቅራቢያው ያለ ሹፌር ያደርሳችዎታል። ያውርዱና ይጀምሩ።',
  app_store_cta: 'በApp Store ያውርዱ',
  play_store_cta: 'በGoogle Play ያግኙት',
  download_on: 'ላይ ያውርዱ',
  get_it_on: 'ላይ ያግኙ',
  choose_car: 'መኪና ይምረጡ',
  schedule_pickup: 'እስከ 90 ቀናት አስቀድመው ማንሳትን ይቀጥሉ',
  extra_wait_time: 'ጉዞዎን ለማሟላት ተጨማሪ የጥበቃ ጊዜ ተካትቷል',
  cancel_policy: 'እስከ 60 ደቂቃ አስቀድመው ነጻ መሰረዝ',
  next: 'ቀጣይ',
  loading_map: 'ካርታ በመጫን ላይ...',
  nearest_driver: 'በአቅራቢያ ያለ ሹፌር',
  drivers_nearby: 'አቅራቢያ ሹፌሮች',
  live: 'ቀጥታ',
  quick_book: 'ፈጣን ማስያዝ',
  start_booking: 'ማስያዝ ጀምር',
  my_account: 'መለያዬ',
  about_us: 'ስለ እኛ',
  contact_us: 'አግኙን',
  privacy_policy: 'የግላዊነት መመሪያ',
  book_ride: 'ጉዞ ይያዙ',
  select_language: 'ቋንቋ ይምረጡ',
  language_updated: 'ቋንቋ ተዘምኗል ወደ',
  reserve: 'ቀጠሮ',
  teens: 'ወጣቶች',
  seniors: 'ሽማግሌዎች',
  book_now: 'አሁን ይዘዙ',
  book_later: 'ቀጠሮ ይያዙ',
  pickup_location: 'መነሻ ቦታ',
  drop_location: 'መድረሻ ቦታ',
  choose_ride: 'ተሽከርካሪ ይምረጡ',
  campaigns: 'ዘመቻዎች',
  recommendations: 'ምክሮች',
  edit_booking: 'ቦኪንግ ያርትዑ',
  view_trip: 'ጉዞ ይመልከቱ',
  driver_assigned: 'ሾፌር ተመድቧል',
  driver_assigned_success: 'ሾፌር በትክክል ተመድቧል',
  active_trip: 'ንቁ ጉዞ',
  active_trip_hint: 'ጉዞ በሂደት ላይ አለ። ዝርዝር ለማየት ይንኩ።',
  view_details: 'ዝርዝር ይመልከቱ',
  no_bookings_yet: 'እስካሁን ምንም ቦኪንግ የለም',
  no_bookings_help: 'የመጀመሪያውን ቦኪንግ በመፍጠር ይጀምሩ።',
  car_types: 'የመኪና አይነቶች',
  security_info: 'ኢሜይል ወይም ስልክዎን በፐርሰናል መረጃ ውስጥ ማስተካከል ይችላሉ። ለደህንነት በOTP እንረጋግጣለን።',
  tip_strong_password: 'ምክር፡ ጠንካራ የይለፍ ቃል ይጠቀሙ እና የአንድ ጊዜ ኮዶችን አትካፈሉ።',
  privacy_blurb: 'ለአካውንትና ጉዞዎች የሚያስፈልገውን ብቻ እንሰብስባለን። መረጃዎን በማንኛውም ጊዜ ማዘመን ወይም ለማየት፣ ለመልቀቅ ወይም ለማጥፋት ከስፖርት ጋር መገናኘት ትችላለህ።',
  request_ride: 'ጉዞ ይጠይቁ',
  pickup_now: 'አሁን አንሳ',
  schedule: 'ቀጠሮ ይያዙ',
  suggestions: 'ምክሮች',
  select_user: 'ተጠቃሚ ይምረጡ',
  enter_pickup: 'የመነሻ ቦታ ያስገቡ',
  enter_destination: 'የመድረሻ ቦታ ያስገቡ',
  see_prices: 'ዋጋ ይመልከቱ',
  add_phone: 'ስልክ ያክሉ',
  add_email: 'ኢሜይል ያክሉ',
  confirm_and_request: 'ያረጋግጡ እና ይጠይቁ',
  payment_mode: 'የክፍያ መንገድ',
  wallet_balance_low: 'የዋሌት ሚዛን ዝቅተኛ ነው',
  wallet_booking_alert: 'ለዚህ ቦኪንግ ዋሌትን ልጠቀም?',
  use_wallet_balance: 'ዋሌት ተጠቀም',
  paynow_button: 'አሁን ክፈል',
  add_pickup_note: 'የመነሻ ማስታወሻ ያክሉ',
  ride_options: 'የጉዞ አማራጮች',
  parcel_types: 'የጥቅል አይነቶች',
  options: 'አማራጮች',
  confirm_ride: 'ጉዞን ያረጋግጡ',
  confirm_pickup_location: 'የመነሻ ቦታን ያረጋግጡ',
  distance: 'ርቀት',
  time: 'ጊዜ',
  fare: 'ዋጋ',
  faster: 'በፍጥነት',
  personal: 'የግል',
  enter_destination_hint: 'ዋጋ ለማየት መድረሻ ያክሉ',
  request: 'ይጠይቁ',
  select_proper: 'ሁሉንም ያስፈልጉ መረጃዎች ያስገቡ',
  place_to_coords_error: 'አድራሻውን ማግኘት አልተቻለም',
  currency_settings: 'የምንዛሬ ቅንብሮች',
  currency_symbol: 'የምንዛሬ ምልክት',
  currency_code: 'የምንዛሬ ኮድ',
  set_decimal: 'ዴሲማል አዘጋጅ',
  swipe_symbol_direction: 'የምልክት አቅጣጫ ይለውጡ',
  disable_online_payments: 'መስመር ላይ ክፍያ አጥፋ',
  disable_cash_payments: 'ጥሬ ገንዘብ ክፍያ አጥፋ',
  maintenance_settings: 'የጥገና ቅንብሮች',
  maintenance_mode: 'የጥገና ሁነታ',
  maintenance_message: 'የጥገና መልዕክት',
  pre_dispatch_window_mins: 'ቀድሞ መላኪያ ጊዜ (ደቂቃ)',
  request_timeout_sec: 'የጥያቄ ጊዜ ጨረሰ (ሰከንድ)',
  dispatch_mode: 'የመላኪያ ሁነታ',
  mode_nearest: 'ቅርብ',
  security_settings: 'የደህንነት ቅንብሮች',
  booking_otp: 'የቦኪንግ OTP',
  driver_approval: 'የሾፌር ፈቃድ',
  car_approval: 'የመኪና ፈቃድ',
  verify_id_password: 'መታወቂያ / የይለፍ ቃል ያረጋግጡ',
  referral_bonus: 'የማጣቀሻ ቦነስ',
  wallet_denominations: 'የዋሌት ዋጋዎች',
  advance_settings: 'ተጨማሪ ቅንብሮች',
  select_country: 'ሀገር ይምረጡ',
  autocomplete_country_restriction: 'የሀገር ገደብ (Autocomplete)',
  allow_multi_country_selection: 'ብዙ ሀገር መምረጥ ፍቀድ',
  convert_to_mile: 'ወደ ማይል ለውጥ',
  repeat_sound_on_new_trip: 'በአዲስ ጉዞ ድምጽ ይድገሙ',
  allow_delivery_pick_image: 'የመነሻ ፎቶ ይፈቅዱ',
  allow_delivery_drop_image: 'የመድረሻ ፎቶ ይፈቅዱ',
  customer_withdraw: 'ደንበኛ ማውጣት',
  car_list_view_horizontal: 'የመኪና ዝርዝር አግድም እይታ',
  use_distance_matrix_api: 'Distance Matrix API ተጠቀም',
  driver_settings: 'የሾፌር ቅንብሮች',
  driver_radius: 'የሾፌር ርቀት',
  auto_dispatch: 'በራስ መላኪያ',
  allow_driver_negative_balance: 'ለሾፌር አሉታዊ ሚዛን ፍቀድ',
  bank_reg_fields: 'የባንክ መዝገቦች',
  show_live_route: 'እውነተኛ መንገድ አሳይ'
};

// Tigrinya – best-effort translations for primary UI strings
const defaultTi = {
  ...defaultEn,
  // Navigation
  ride: 'ጉዕዞ',
  drive: 'ምዝዋር',
  about: 'ብዛዕባ',
  company: 'ትካል',
  products: 'ፍርያት',
  business: 'ንግዲ',
  privacy: 'ብሕትውና',
  terms: 'ውዕላት',
  language: 'ቋንቋ',
  // Auth
  signIn: 'እቶ',
  login: 'እቶ',
  signup: 'ተመዝገብ',
  enter_email_or_mobile: 'ቁጽሪ ተሌፎን ወይ ኢሜል ኣእቱ',
  contact_placeholder: 'ቁጽሪ ተሌፎን ወይ ኢሜል',
  we_will_send_otp: 'ናይ ምርግጋጽ ኮድ ንልኣኽ',
  continue: 'ቀጽል',
  resend_otp: 'ኮድ ደጊምካ ላኣኽ',
  resend_in: 'ደጊምካ ላኣኽ ብ',
  enter_code: 'ኮድ ኣእቱ',
  code_sent_to: 'ኮድ ሰዲድናልኩም ናብ',
  verify: 'ኣረጋግጽ',
  back: 'ተመለስ',
  signin_with_vipps: 'ብVipps ቀጽል',
  signin_with_google: 'ብGoogle ቀጽል',
  signin_with_facebook: 'ብFacebook ቀጽል',
  use_password_login: 'ብይሕለፈቓል እቶ',
  password_login_description: 'ብኢሜል እይሕለፈቓል እቶ',
  email_placeholder: 'ኢሜል አድራሻ',
  password: 'ይሕለፈቓል',
  create_account: 'ኣካውንት ፍጠር',
  forgot_password: 'ይሕለፈቓል ረሲዕኩም?',
  use_otp_login: 'ብናይ ምርግጋጽ ኮድ እቶ',
  sign_up: 'ተመዝገብ',
  signup_description: 'ንምጅማር ተመዝገቡ',
  first_name: 'ስም',
  last_name: 'ስም አቦ',
  or: 'ወይ',
  // Dialogs
  alert: 'ምጥቃዕ',
  ok: 'ሕራይ',
  cancel: 'ሰርዝ',
  not_logged_in: 'ኣይኣቶኹምን',
  // Buttons
  learn_more: 'ዝያዳ ፍለጡ',
  get_started: 'ጀምሩ',
  // Common
  home: 'ገዛ',
  my_bookings: 'ቦኪንጎችየ',
  wallet: 'ቦኪት',
  book_a_ride: 'ጉዕዞ ሕተት',
  drive_with_us: 'ምሳና ንዘው',
  why_choose_plukk: 'ስለምንታይ ፕሉክ ይምረጹ',
  security: 'ድሕነት',
  privacy_data: 'ናይ ብሕትና',
  apply_language: 'ቋንቋ ኣዘምን',
  get_there: 'ናብኡ ብጻሕ',
  avg_pickup_time: 'ማእከላይ ናይ ምልዓል ግዜ',
  available: 'ይርከብ',
  active_drivers: 'ንጡፋት ዘወርቲ',
  simple_process: 'ቀሊል መስርሕ',
  why_us: 'ስለምንታይ ንሕና',
  available_now: 'ሕጂ ይርከብ',
  free_download: 'ነጻ',
  download_app: 'ኣፕ ኣውርድ',
  weekly_payouts: 'ሰሙናዊ ክፍሊት',
  detailed_analytics: 'ዝርዝር ትንተና',
  earn_on_your_terms: 'ብናትካ መደብ ገንዘብ ስራሕ',
  // Footer translations
  quick_links: 'ቅልጡፍ ሊንክታት',
  follow_us: 'ተኸተሉና',
  myaccount: 'ሕሳበይ',
  // Landing page translations
  hero_title: 'ብፕሉክ የትም ኪድ',
  book_your_title: 'ብፕሉክ ናብ ዝኾነ ቦታ ኪድ',
  drivers_near_you: 'ኣብ አቅራቢያኻ ዘለዉ ሾፌራት',
  about_us_content2: 'ጉዕዞ ሕተት፣ ደይብ፣ ኪድ።',
  book_your_ride_menu: 'ጉዕዞ ሕተት',
  how_it_works: 'ከመይ ይሰርሕ',
  how_plukk_works: 'ፕሉክ ከመይ ይሰርሕ',
  hiw_subtitle: 'ብውሑድ ምጥዋቕ ጉዕዞ ረኸብ',
  hiw_step1_title: 'መዕረፊኻ ኣእቱ',
  hiw_step1_desc: 'ኣፕ ክፈት፣ ናበይ ክትከይድ ከምዝደለኻ ኣእቱ፣ ዘለዉ ኣማራጺታት ከነርእየካ ኢና።',
  hiw_step2_title: 'ጉዕዞኻ ምረጽ',
  hiw_step2_desc: 'ናይ ጉዕዞ ኣማራጺታት፣ ዋጋታት፣ ናይ ምብጻሕ ግዜ ኣወዳድር። ንዓኻ ዝሰማማዕ ምረጽ።',
  hiw_step3_title: 'ክፈል እሞ ኪድ',
  hiw_step3_desc: 'ጉዕዞኻ ኣረጋግጽ፣ ዘወርቲኻ ብቐጥታ ተኸታተል፣ ብቐሊሉ ክፈል።',
  ride_with_plukk: 'ምስ Plukk ጉዕዞ ግበር',
  ride_with_plukk_desc: 'ጉዕዞ ኣለባስ፣ ቅርብ ሾፌር ብጥቂት ደቒቕ ይመጽእ።',
  drive_with_plukk: 'ምስ Plukk ንዘው',
  drive_with_plukk_desc: 'ሰዓትኻ ኣዘጋጅ፣ ብትፈልጥ ግዜ ገንዘብ ስራሕ።',
  get_a_ride: 'ጉዕዞ ሕተት',
  become_a_driver: 'ሾፌር ኩን',
  friendly_driver: 'ትሑት ሾፌር',
  safety_first: 'ድሕነት ቀዳም እዩ',
  safety_desc: 'ኣብ ጀርባ ወይ ኣብ ኩርሻ ትቆም ብድሕነትካ ንድሕነ ኢና። ሰባትን ቴክኖሎጂን ኣጥቕብና ጉዕዞታት ንምዕቃቅል።',
  learn_about_safety: 'ብዛዕባ ድሕነት ተማሃር',
  driver_screening: 'ምርመራ ሾፌር',
  driver_screening_desc: 'ቅድሚ ምዝዋር ፍተሻ ታሪኽን መረጃን',
  real_time_support: 'ቀጥታ ድጋፍ',
  real_time_support_desc: '24/7 ናይ ደንበኛ ኣገልግሎት',
  trip_tracking: 'ናይ ጉዕዞ ክትትል',
  trip_tracking_desc: 'ናይ ጉዕዞ ሁኔታኻ ምስ ቤተሰብ ካፍል',
  vp_safety: 'ድሕነት መጀመርታ',
  vp_safety_desc: 'ኩሉ ጉዕዞ ዝተረጋገጹ ዘወርቲ ኣብ ኣፕ ድጋፍ ይህብ።',
  vp_upfront: 'ንጹር ዋጋ',
  vp_upfront_desc: 'ዋጋ ቅድሚ ርአ — ዝኾነ ኣስደናቒ የለን።',
  vp_schedule: 'ዝተቀጸሉ ምልዓል',
  vp_schedule_desc: 'ንሰላም ኣእምሮ ጉዕዞታት ቅድሚ ሓዝ።',
  vp_support: '24/7 ኣብዚ ኣለና',
  vp_support_desc: 'ሓገዝ ምስ ዘድልየካ ተሓባባሪ ድጋፍ።',
  vp_business: 'ናይ ንግዲ ሕሳባት',
  vp_business_desc: 'ንቡድናት ወርሓዊ ክፍሊት ን ናይ ጉዕዞ ጸብጻባት።',
  vp_green: 'ኣረንጓዴ ኣማራጺታት',
  vp_green_desc: 'ክርከብ ከሎ ትሑት ልቀት መካይን ምረጽ።',
  vp_subtitle: 'ቅልጡፍ ምልዓል፣ ፍትሓዊ ዋጋ፣ ኣጋዛዚ ድጋፍ።',
  drive_when_you_want: 'ክትደሊ ከለኻ ንዘው',
  drive_subtitle: 'ብናትካ መደብ ገንዘብ ስራሕ። ንምዝዋር ተመዝገብ።',
  driver_signup_subtitle: 'ብትደልዮ ግዜ ንዘው፣ ዝትፈልጥዎ ገንዘብ ስራሕ። ሰዓትኻ ኣስተዳድር እና ዘለኻን ብዙሕ ከፍለሉ።',
  grow_taxi_business: 'ፍሊትካ ኣዕቢ',
  taxi_subtitle: 'መካይንካ ኣመሓድር፣ ኣፈጻጽማ ተኸታተል፣ ንግድኻ ኣዕቢ።',
  plukk_for_business: 'ፕሉክ ንንግዲ',
  business_desc: 'ናይ ኩባንያኻ መንቀሳቀሲ ቀሊል ኣድርጎ ይዓይን። ንኹሉ መጠን ቀሊል ዘለዎ ናይ ጉዕዞ ምምሕዳር ይህብ።',
  simplified_billing: 'ቀሊል ክፍሊት',
  simplified_billing_desc: 'ንኩሉ ጉዕዞታት ሓደ ወርሃዊ ደረሰኝ',
  central_dashboard: 'ማእኸላይ ዳሽቦርድ',
  central_dashboard_desc: 'ጉዕዞታት ተኸታተልን ተጠቃሚታት ኣስተዳድርን',
  expense_integration: 'ወጻኢት ምትዛዝብ',
  expense_integration_desc: 'ናይ ወጻኢ መሳርሒታትካ ምስ ይስራሕ',
  become_partner: 'መሻርኽቲ ኩን',
  best_service_provider: 'ስለምንታይ ዘወርቲ ይመርጹና',
  flexible_hours: 'ተዓጻጻፊ ሰዓታት',
  flexible_hours_desc: 'ክትደሊ ከለኻ ንዘው። ናትካ መደብ ግበር።',
  upfront_fares: 'ቅድሚት ዋጋታት',
  upfront_fares_desc: 'ቅድሚ ምቕባልካ ናይ ጉዕዞ ዋጋ ርአ። ንጹር ዋጋ ጥራይ።',
  add_vehicles: 'መካይን ን ዘወርቲ ወስኽ',
  fleet_dashboard: 'ናይ ፍሊት ዳሽቦርድ',
  download_apps_title: 'ኣፕታት ኣውርዱ እሞ ጀምሩ',
  download_apps_subtitle: 'ን iOSን Androidን ይርከብ። ዝምልከት ዝርከብኩም ኣፕ ምረጹ።',
  mobile_apps_on_store: 'ኣፕ ረኸብ',
  app_store_deception1: '📱 የPLUKK መተግበሪያ አውርድ - ንዓማዊልን ንመዘውትርን | 🤖 ብGoogle Play ርከቦ (Android) | 🍎 ብApp Store አውርድ (iOS) | 💻 ናይ መቆጻጸሪ ፓነል ብድረገጽ ይርከብ',
  download_on: 'ኣብ ኣውርድ',
  get_it_on: 'ኣብ ረኸብ',
  choose_car: 'መኪና ምረጽ',
  schedule_pickup: 'ክሳብ 90 መዓልቲ ቅድሚ ምልዓል ቆጽር',
  extra_wait_time: 'ጉዕዞኻ ንምርካብ ተወሳኺ ናይ ምጽባይ ግዜ ተሓዊሱ',
  cancel_policy: 'ክሳብ 60 ደቒቕ ቅድሚ ነጻ ምስራዝ',
  next: 'ዝቕጽል',
  loading_map: 'ካርታ ይጽዕን ኣሎ...',
  nearest_driver: 'ዝቐረበ ሾፌር',
  drivers_nearby: 'ኣብ አቅራቢያ ሾፌራት',
  live: 'ቀጥታ',
  quick_book: 'ቅልጡፍ ምሓዝ',
  start_booking: 'ምሓዝ ጀምር',
  my_account: 'ሕሳበይ',
  about_us: 'ብዛዕባና',
  contact_us: 'ርኸቡና',
  privacy_policy: 'ናይ ብሕትና ፖሊሲ',
  book_ride: 'ጉዕዞ ሓዝ',
  select_language: 'ቋንቋ ምረጽ',
  language_updated: 'ቋንቋ ተዘሚኑ ናብ',
  reserve: 'ቀጠሮ',
  teens: 'ወጣት ሰባት',
  seniors: 'ሽማግሌታት',
  book_now: 'ሕጂ ይዘዙ',
  book_later: 'ቀጠሮ ይውሰዱ',
  pickup_location: 'መነሻ ቦታ',
  drop_location: 'መድረሻ ቦታ',
  choose_ride: 'መኪና ምረጽ',
  campaigns: 'ዘመቻታት',
  recommendations: 'ምክሮታት',
  edit_booking: 'ቦኪንግ ምትእስሳር',
  view_trip: 'ጉዞ ምርኣይ',
  driver_assigned: 'ሾፌር ተመድቦ ኣሎ',
  driver_assigned_success: 'ሾፌር ብሓጎስ ተመድቦ',
  active_trip: 'ንቁ ጉዞ',
  active_trip_hint: 'ጉዞ ኣሎ ብሂደት። ዝርዝር ንምርኣይ ጠውቑ።',
  view_details: 'ዝርዝር ምርኣይ',
  no_bookings_yet: 'እዚ ድማ ቦኪንግ የለን',
  no_bookings_help: 'ብመጀመርያ ቦኪንግኩም ጀምሩ።',
  car_types: 'ዓይነት መኪናታት',
  security_info: 'ኢመይል ወይ ስልኪ ኣብ ግል መረዳእታ ትቐይሩ ትኽእሉ። ናይ ደህንነት ምክንያት ብOTP ንረጋግጽ።',
  tip_strong_password: 'መንገዲ፡ ጸንተይ ትእዛዝ ተጠቀም እና OTP ኮድ ሓደሽዎ ኣትካፈል።',
  privacy_blurb: 'ንኻውንትኩምን ንጉዞታትኩምን ዝያዳ ዝድልየ መረጃ ጥራይ እንሰብስብ። መረጃኹም ብዝኾነ ግዜ ትደምስሱ ትኽእሉ፣ ወይስ ናብ ስፖርት ብምቕጽል ምርኣይ፣ ምውጣት ወይ ምድምሳር ትጠይቑ።',
  request_ride: 'ጉዞ ምዕዳግ',
  pickup_now: 'ሕጂ ኣንስኦ',
  schedule: 'ቀጠሮ ይውሰዱ',
  suggestions: 'ምክሮታት',
  select_user: 'ተጠቃሚ ምረፅ',
  enter_pickup: 'መነሻ ቦታ ኣእትው',
  enter_destination: 'መድረሻ ኣእትው',
  see_prices: 'ዋጋታት ምርኣይ',
  add_phone: 'ስልኪ ኣክል',
  add_email: 'ኢመይል ኣክል',
  confirm_and_request: 'ኣረጋግፅን ኣለባስ ምግዳብ',
  payment_mode: 'ዘይቲ ክፍሊት',
  wallet_balance_low: 'ሚዛን ቦኪት ዝተንከረ',
  wallet_booking_alert: 'እዚ ቦኪንግ ብቦኪት ክኽፈል?',
  use_wallet_balance: 'ቦኪት ተጠቀም',
  paynow_button: 'ሕጂ ክፈል',
  add_pickup_note: 'መልእኽቲ ምስ ኣነስክን ኣክል',
  ride_options: 'ኣማራጺታት',
  parcel_types: 'ዓይነት ጥቕል',
  options: 'ኣማራጺታት',
  confirm_ride: 'ጉዞ ኣረጋግፅ',
  confirm_pickup_location: 'መነሻ ቦታ ኣረጋግፅ',
  distance: 'ርዝሒ',
  time: 'ግዜ',
  fare: 'ዋጋ',
  faster: 'ብፍጥነት',
  personal: 'ግል',
  enter_destination_hint: 'ዋጋ ንምርኣይ መድረሻ ኣእትው',
  request: 'ኣለባስ',
  select_proper: 'ሓገዝታት ሙሉእ ምምላእ ይኽእል',
  place_to_coords_error: 'ኣድራሻ ምርኣይ ኣይተቻለን'
};

const storedLang = (()=>{ try { return typeof window !== 'undefined' ? localStorage.getItem('lang') : null; } catch(e){ return null; } })();

i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: { translations: defaultEn },
      nb: { translations: defaultNb },
      no: { translations: defaultNb },
      es: { translations: defaultEs },
      fr: { translations: defaultFr },
      ar: { translations: defaultAr },
      it: { translations: defaultIt },
      zh: { translations: defaultZh },
      ja: { translations: defaultJa },
      am: { translations: defaultAm },
      ti: { translations: defaultTi }
    },
    fallbackLng: 'en',
    lng: storedLang || undefined,
    ns: ['translations'],
    defaultNS: 'translations',
    interpolation: { escapeValue: false },
  });

function App() {
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || GoogleMapApiConfig;
  useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: googleMapsApiKey,
    libraries: libraries
  })

  return (
    <Provider store={store}>
      <FirebaseProvider config={FirebaseConfig}>
        <ThemedApp />
      </FirebaseProvider>
    </Provider>
  );
}

function ThemedApp() {
  const settings = useSelector(state => state.settingsdata.settings);
  const themeConfig = useMemo(() => resolveAdminTheme(settings), [settings]);
  const theme = useMemo(() => createTheme(themeConfig), [themeConfig]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SnackbarProvider>
        <AuthLoading>
          <BrowserRouter>
              <Routes>
                <Route path="/dashboard" element={<ProtectedRoute permit={"admin,fleetadmin,supervisor,partner,partner_manager,fleet_owner,fleet_manager,taxi_owner"} requiredPermission={{ module: 'dashboard', action: 'read' }}><Dashboard /></ProtectedRoute>}/>
                <Route path="/bookings" element={<ProtectedRoute permit={"customer,admin,driver,fleetadmin,supervisor,partner,partner_manager,fleet_owner,fleet_manager,taxi_owner"} requiredPermission={{ module: 'bookings', action: 'read' }}><BookingHistory /></ProtectedRoute>}/>
                <Route path="/profile" element={<ProtectedRoute permit={"customer,admin,driver,fleetadmin"}><MyProfile /></ProtectedRoute>}/>
                <Route path="/cartypes" element={<ProtectedRoute permit={"admin,supervisor,fleetadmin,fleet_owner,fleet_manager,taxi_owner"} requiredPermission={{ module: 'fleet', action: 'update' }}><CarTypes /></ProtectedRoute>}/>
                <Route path="/cars" element={<ProtectedRoute permit={"admin,fleetadmin,driver,supervisor,fleet_owner,fleet_manager,taxi_owner,taxi_driver"} requiredPermission={{ module: 'fleet', action: 'read' }}><CarsList /></ProtectedRoute>}/>
                <Route path="/cancelreasons" element={<ProtectedRoute permit={"admin,supervisor"} requiredPermission={{ module: 'settings', action: 'update' }}><CancellationReasons /></ProtectedRoute>}/>
                <Route path="/addbookings" element={<ProtectedRoute permit={"admin,fleetadmin,customer,supervisor,partner,partner_manager,fleet_owner,fleet_manager,taxi_owner"} requiredPermission={{ module: 'bookings', action: 'create' }}><AddBookings /></ProtectedRoute>}/>
                <Route path="/promos" element={<ProtectedRoute permit={"admin,supervisor"} requiredPermission={{ module: 'settings', action: 'update' }}><Promos /></ProtectedRoute>}/>
                <Route path="/users" element={<ProtectedRoute permit={"admin,fleetadmin,supervisor,partner_manager,fleet_owner,fleet_manager,taxi_owner"} requiredPermission={{ module: 'users', action: 'read' }}><Users /></ProtectedRoute>}/>
                <Route path="/users/riders" element={<ProtectedRoute permit={"admin,fleetadmin,supervisor,partner_manager,fleet_owner,fleet_manager,taxi_owner"} requiredPermission={{ module: 'users', action: 'read' }}><Riders /></ProtectedRoute>}/>
                <Route path="/users/drivers" element={<ProtectedRoute permit={"admin,fleetadmin,supervisor,partner_manager,fleet_owner,fleet_manager,taxi_owner"} requiredPermission={{ module: 'users', action: 'read' }}><Drivers /></ProtectedRoute>}/>
                <Route path="/users/fleetadmins" element={<ProtectedRoute permit={"admin,supervisor"} requiredPermission={{ module: 'users', action: 'update' }}><FleetAdmins /></ProtectedRoute>}/>
                <Route path="/users/admins" element={<ProtectedRoute permit={"admin,supervisor"} requiredPermission={{ module: 'users', action: 'update' }}><CreateAdmin /></ProtectedRoute>}/>
                <Route path="/driverearning" element={<ProtectedRoute permit={"admin,fleetadmin,supervisor,partner_manager,fleet_owner,fleet_manager,taxi_owner"} requiredPermission={{ module: 'reports', action: 'read' }}><DriverEarning /></ProtectedRoute>}/>
                <Route path="/notifications" element={<ProtectedRoute permit={"admin,supervisor"} requiredPermission={{ module: 'notifications', action: 'read' }}><Notifications /></ProtectedRoute>}/>
                <Route path="/earningreports" element={<ProtectedRoute permit={"admin,supervisor"} requiredPermission={{ module: 'reports', action: 'read' }}><Earningreports /></ProtectedRoute>}/>
                <Route path="/taxi-owners" element={<ProtectedRoute permit={"admin,fleetadmin,supervisor,fleet_owner,fleet_manager,taxi_owner"} requiredPermission={{ module: 'reports', action: 'read' }}><TaxiOwners /></ProtectedRoute>}/>
                <Route path="/taxi-information" element={<ProtectedRoute permit={"admin,fleetadmin,supervisor,fleet_owner,fleet_manager,taxi_owner"} requiredPermission={{ module: 'reports', action: 'read' }}><TaxiInformation /></ProtectedRoute>}/>
                <Route path="/income-shift-report" element={<ProtectedRoute permit={"admin,fleetadmin,supervisor,fleet_owner,fleet_manager,taxi_owner"} requiredPermission={{ module: 'reports', action: 'read' }}><IncomeShiftReport /></ProtectedRoute>}/>
                <Route path="/competitor-prices" element={<ProtectedRoute permit={"admin,fleetadmin,supervisor,fleet_owner,fleet_manager,taxi_owner"} requiredPermission={{ module: 'reports', action: 'read' }}><CompetitorPrices /></ProtectedRoute>}/>
                <Route path="/turpris/match" element={<TurPrisMatch />}/>
                <Route path="/turpris/landing" element={<TurPrisLanding />}/>
                <Route path="/turpris/admin" element={<ProtectedRoute permit={"admin,supervisor"} requiredPermission={{ module: 'settings', action: 'update' }}><TurPrisAdmin /></ProtectedRoute>}/>
                <Route path="/turpris" element={<TurPrisLanding />}/>
                <Route path="/partner/:partnerCode" element={<PartnerPortal />}/>
                <Route path="/partners/:partnerSlug/book" element={<ProtectedRoute permit={"customer,admin,fleetadmin,partner,partner_manager,fleet_owner,fleet_manager,taxi_owner"} requiredPermission={{ module: 'bookings', action: 'create' }}><PartnerBookingPage /></ProtectedRoute>}/>
                <Route path="/partners/:partnerSlug" element={<PartnerLandingPage />} />
                <Route path="/book" element={<ProtectedRoute permit={"customer,admin,fleetadmin,partner,partner_manager,fleet_owner,fleet_manager,taxi_owner"} requiredPermission={{ module: 'bookings', action: 'create' }}><PartnerBookingPage /></ProtectedRoute>}/>
                <Route path="/:partnerSlug/book" element={<ProtectedRoute permit={"customer,admin,fleetadmin,partner,partner_manager,fleet_owner,fleet_manager,taxi_owner"} requiredPermission={{ module: 'bookings', action: 'create' }}><PartnerBookingPage /></ProtectedRoute>}/>
                <Route path="/:partnerSlug" element={<PartnerLandingPage />} />
                <Route path="/addtowallet" element={<ProtectedRoute permit={"admin,supervisor"} requiredPermission={{ module: 'finance', action: 'create' }}><AddMoney /></ProtectedRoute>}/>
                <Route path="/userwallet" element={<ProtectedRoute permit={"customer,driver,taxi_driver"} requiredPermission={{ module: 'finance', action: 'read' }}><UserWallet /></ProtectedRoute>}/>
                <Route path="/withdraws" element={<ProtectedRoute permit={"admin,supervisor"} requiredPermission={{ module: 'finance', action: 'update' }}><Withdraws /></ProtectedRoute>}/>
                <Route path="/allreports" element={<ProtectedRoute permit={"admin,fleetadmin,supervisor,partner_manager,fleet_owner,fleet_manager,taxi_owner"} requiredPermission={{ module: 'reports', action: 'read' }}><AllReports /></ProtectedRoute>}/>
                <Route path="/settings" element={<ProtectedRoute permit={"admin,fleetadmin,supervisor,partner,partner_manager,fleet_owner,fleet_manager,taxi_owner"} requiredPermission={{ module: 'settings', action: 'read' }}><Settings /></ProtectedRoute>}/>
                <Route path="/storefront" element={<ProtectedRoute permit={"admin,fleetadmin,supervisor"} requiredPermission={{ module: 'settings', action: 'update' }}><Storefront /></ProtectedRoute>}/>
                <Route path="/languagesettings" element={<ProtectedRoute permit={"admin,supervisor"} requiredPermission={{ module: 'settings', action: 'update' }}><LanguageSettings /></ProtectedRoute>}/>
                <Route path="/zones" element={<ProtectedRoute permit={"admin,supervisor"} requiredPermission={{ module: 'settings', action: 'update' }}><Zones /></ProtectedRoute>}/>
                <Route path="/appinfo" element={<ProtectedRoute permit={"admin,supervisor"} requiredPermission={{ module: 'settings', action: 'read' }}><AppInformation /></ProtectedRoute>}/>
                <Route path="/partners/:partnerSlug/shop" element={<Shop />} />
                <Route path="/partners/:partnerSlug/shop/category/:categoryId" element={<ShopCategory />} />
                <Route path="/partners/:partnerSlug/shop/:productId" element={<ShopProduct />} />
                <Route path="/partners/:partnerSlug/cart" element={<ShopCart />} />
                <Route path="/partners/:partnerSlug/checkout" element={<ShopCheckout />} />
                <Route path="/partners/:partnerSlug/subscriptions" element={<Subscriptions />} />
                <Route path="/partners/:partnerSlug/auctions" element={<Auctions />} />
                <Route path="/partners/:partnerSlug/auctions/:auctionId" element={<AuctionDetail />} />
                <Route path="/:partnerSlug/shop" element={<Shop />} />
                <Route path="/:partnerSlug/shop/category/:categoryId" element={<ShopCategory />} />
                <Route path="/:partnerSlug/shop/:productId" element={<ShopProduct />} />
                <Route path="/:partnerSlug/cart" element={<ShopCart />} />
                <Route path="/:partnerSlug/checkout" element={<ShopCheckout />} />
                <Route path="/:partnerSlug/subscriptions" element={<Subscriptions />} />
                <Route path="/:partnerSlug/auctions" element={<Auctions />} />
                <Route path="/:partnerSlug/auctions/:auctionId" element={<AuctionDetail />} />
                <Route path="/shop" element={<Shop />} />
                <Route path="/shop/category/:categoryId" element={<ShopCategory />} />
                <Route path="/shop/:productId" element={<ShopProduct />} />
                <Route path="/cart" element={<ShopCart />} />
                <Route path="/checkout" element={<ShopCheckout />} />
                <Route path="/subscriptions" element={<Subscriptions />} />
                <Route path="/auctions" element={<Auctions />} />
                <Route path="/auctions/:auctionId" element={<AuctionDetail />} />
                <Route path="/contact-us" element={<ContactUs />} />
                <Route path="/about-us" element={<AboutUs />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/" element={<LandingPage />} />
              </Routes>
              {/* Bottom spacer so content isn't obscured by MobileNav on small screens */}
              <Box sx={{ height: 88, display: { xs: 'block', md: 'none' } }} />
              <MobileNav />
          </BrowserRouter>
        </AuthLoading>
      </SnackbarProvider>
    </ThemeProvider>
  );
}

export default App;
